import { initContract } from "@ts-rest/core";
import { z } from "zod";

// ===========================================================================
// El contrato: lo único que el frontend y el servidor comparten.
//
// Es un paquete aparte y no un archivo del servidor porque el frontend lo
// importa igual que el backend. De ahí sale el tipado extremo a extremo: si
// alguien cambia la forma de una respuesta, el error aparece en la pantalla
// que la consume, en tiempo de compilación, y no en producción.
// ===========================================================================

const c = initContract();

const errorBody = z.object({
  code: z.string(),
  message: z.string(),
});

/**
 * El filtro por importe de la ficha de un extracto.
 *
 * Contabilidad lo pidió con la forma de la pantalla: «me sale primero la
 * casilla de mayor, menor o igual, y a la derecha la casilla donde ingreso el
 * número». Buscar «300» en la descripción no sirve para esto: el importe no
 * está en el texto, y un movimiento de 1.300 saldría igual.
 *
 * EL MONTO VA EN UNIDADES ENTERAS —soles o dólares, no céntimos— y la
 * comparación REDONDEA. Es lo que pidió el área para «que no haya problemas
 * decimales»: escribir 300 tiene que encontrar el abono de 300.00 y también el
 * de 299.99, porque quien busca un pago de trescientos no sabe —ni tiene por
 * qué— si el banco le puso dos céntimos de diferencia.
 *
 * Se comparte entre la ficha y la consulta de identificadores porque marcar
 * tiene que alcanzar exactamente lo que el filtro deja ver: dos definiciones
 * del mismo filtro acabarían marcando lo que no se ve.
 */
const porImporte = {
  /**
   * En qué columna se busca. `abono` por defecto —«por default es entró»—,
   * que es el importe por el que se pregunta casi siempre: lo que hay que
   * conciliar es lo que entra.
   */
  campo: z.enum(["abono", "cargo", "saldo", "itf"]).optional(),
  comparar: z.enum(["mayor", "menor", "igual"]).optional(),
  /** En unidades enteras, no en céntimos. Puede ser negativo: el saldo lo es. */
  monto: z.coerce.number().int().optional(),
};

export const usuarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(["administrador", "gerente", "jefe", "operativo", "visor"]),
});

/**
 * Una línea del extracto tal como se lee y como se puede corregir.
 *
 * Es la misma forma que `MovimientoCrudo` del motor
 * (`packages/motor/src/extractos/tipos.ts`), repetida aquí porque el contrato no
 * puede depender de tipos internos del motor: lo que viaja por el cable tiene
 * que estar declarado en el cable. Si las dos se separan, la validación del
 * servidor lo dice — `guardarFilas` valida contra la del motor antes de escribir.
 *
 * IMPORTES EN CÉNTIMOS ENTEROS, como en toda la base. `abono` y `cargo` son
 * excluyentes y los dos POSITIVOS: la dirección la dice cuál viene relleno.
 */
export const filaExtractoSchema = z.object({
  linea: z.number().int().nonnegative(),
  pagina: z.number().int().positive(),
  fechaOper: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  fechaValor: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  descripcion: z.string(),
  referencia: z.string().nullable(),
  canal: z.string().nullable(),
  nroOperacion: z.string().nullable(),
  abono: z.number().int().nonnegative().nullable(),
  cargo: z.number().int().nonnegative().nullable(),
  /**
   * El ITF que el banco cobra EN LA MISMA LÍNEA del movimiento.
   *
   * El BBVA lo imprime como una columna más y el saldo de esa línea ya lo lleva
   * descontado. `null` en los bancos que no lo desglosan.
   */
  itf: z.number().int().nonnegative().nullable(),
  saldo: z.number().int().nullable(),
});

/** El cuadre de un extracto: lo declarado contra lo que sale de sumar. */
export const cuadreSchema = z.object({
  declaradoAbonos: z.number().int().nullable(),
  calculadoAbonos: z.number().int(),
  declaradoCargos: z.number().int().nullable(),
  calculadoCargos: z.number().int(),
  cuadra: z.boolean(),
});

/** Un problema encontrado al validar un extracto. */
export const hallazgoSchema = z.object({
  codigo: z.string(),
  gravedad: z.enum(["error", "aviso"]),
  mensaje: z.string(),
});

/**
 * Lo que la pantalla de inicio necesita para decir en qué estado está el
 * módulo. Los importes van en céntimos enteros, como en toda la base: el
 * frontend los formatea al mostrarlos, nunca antes.
 */
export const resumenSchema = z.object({
  empresas: z.number().int(),
  cuentas: z.number().int(),
  cargas: z.number().int(),
  movimientos: z.number().int(),
  movimientosSinCruzar: z.number().int(),
});

/**
 * Lo que la pantalla de inicio necesita para decir «esto es lo que falta».
 *
 * La propuesta de diseño lo dice en una regla: un número, una frase y un botón.
 * Así que el servidor manda ya calculado lo que se muestra, en vez de que la
 * pantalla junte cinco llamadas y haga cuentas.
 */
export const inicioSchema = z.object({
  periodo: z.string(),

  /** Los tiles de arriba: «cómo va el cierre». */
  conciliado: z.object({
    porcentaje: z.number(),
    importe: z.number().int(),
    total: z.number().int(),
    /**
     * CUÁNTOS abonos se cerraron, no cuánto dinero.
     *
     * Faltaba, y su ausencia se notaba: la vista sencilla quería decir cuántos
     * había cerrado el sistema, no tenía la cifra, y la calculaba restando
     * `totalPagos - esperanDecision`. Con 2.089 abonos y 77 en la cola, eso
     * daba «cerró 2.012 sin preguntar nada» — cuando había cerrado 191, y la
     * misma pantalla decía tres centímetros más abajo que iba por el 5,7 % del
     * importe. Una cifra que se deduce mal es peor que una que falta.
     */
    cuantos: z.number().int(),
  }),
  /**
   * Todos los abonos pendientes de decidir. Es la cifra grande y honesta.
   *
   * No confundir con `esperanDecision`: esto es TODO lo que sigue abierto.
   */
  necesitaPersona: z.number().int(),
  /**
   * LOS DOS INDICADORES QUE PIDIÓ CONTABILIDAD, contados y no supuestos.
   *
   * `conciliados` es lo que el área llama conciliado: un movimiento con su
   * documento detrás —voucher Y boleta—, no solo un cruce. `conciliado.cuantos`
   * cuenta cruces y no distingue si hay papel.
   *
   * `sinDocumento` son los abonos que ningún voucher explica. Es el grueso de
   * lo pendiente y no salía en ninguna cifra de la portada.
   */
  indicadores: z.object({
    conciliados: z.number().int(),
    sinDocumento: z.number().int(),
  }),
  /** Con qué se puede filtrar, sacado de lo que hay. */
  opciones: z.object({
    cuentas: z.array(
      z.object({
        id: z.string(),
        banco: z.string(),
        numero: z.string(),
        moneda: z.enum(["PEN", "USD"]),
      }),
    ),
    empresas: z.array(z.object({ id: z.string(), razonSocial: z.string() })),
  }),
  /**
   * Los que la revisión guiada va a preguntar, uno por uno.
   *
   * Es un SUBCONJUNTO de `necesitaPersona`: solo entran los que además tienen
   * al menos un candidato que proponer (`colaDeRevision`). Los demás siguen
   * pendientes y no se pueden revisar de uno en uno porque no hay nada que
   * enseñar al lado.
   */
  esperanDecision: z.number().int(),
  prestamosEntreEmpresas: z.number().int(),
  sinRespaldo: z.object({
    cuantos: z.number().int(),
    importe: z.number().int(),
  }),

  /** La serie del sparkline: vouchers leídos por día. */
  vouchersPorDia: z.array(
    z.object({ dia: z.string(), cuantos: z.number().int() }),
  ),
  adjuntos: z.number().int(),
  /**
   * Cuántos expedientes les falta algo. Es lo que la lámina enseña al lado de
   * «Docs. de clientes», y lo que se va a pedir.
   *
   * Ahí iba `adjuntos`, que cuenta IMÁGENES extraídas de los PDF: con cuatro
   * expedientes cargados la lámina decía 35 y la pantalla decía 4. Los dos
   * números eran ciertos y contaban cosas distintas bajo el mismo rótulo.
   */
  expedientesIncompletos: z.number().int(),
  /**
   * Expedientes que siguen teniendo trabajo en «Conciliar expediente».
   *
   * Es la MISMA condición que la cola de esa pantalla, no una parecida: un
   * contador que diga otro número que la pantalla a la que lleva hace dudar
   * de los dos.
   */
  expedientesPorConciliar: z.number().int(),
  imagenesDistintas: z.number().int(),

  /**
   * Cómo va la lectura, y cuánto ha costado.
   *
   * Contabilidad pidió «verlo en un dashboard»: aciertos, cuánto se corrige a
   * mano y coste por pasada. Esto es la parte que HOY tiene datos. La otra
   * —los aciertos por campo— necesita que alguien verifique vouchers, y ese
   * número va aquí precisamente para que se vea que está en cero: mientras lo
   * esté, no hay con qué medir si el modelo mejora.
   */
  lectura: z.object({
    /** Imágenes distintas con lectura guardada. */
    leidas: z.number().int(),
    /** Las que resolvió el lector óptico, que es local y no se cobra. */
    porOptico: z.number().int(),
    /** Las que hubo que subir al modelo. */
    porModelo: z.number().int(),
    /** Dólares gastados, de las lecturas que lo tienen anotado. */
    costo: z.number(),
    /** Cuántas lecturas traen su coste. Las anteriores al arreglo, no. */
    conCosto: z.number().int(),
    /**
     * Veredictos de una persona sobre lo que entendió el lector.
     *
     * Es el conjunto de entrenamiento y el que desbloquea la calibración del
     * score. En cero significa que todavía no hay nada contra qué medir.
     */
    verificadas: z.number().int(),
  }),

  /** Las tres bandejas, con su recuento y su explicación. */
  bandejas: z.array(
    z.object({
      nombre: z.string(),
      tono: z.enum(["ok", "warn", "bad"]),
      cuantos: z.number().int(),
      porcentaje: z.number(),
      explicacion: z.string(),
    }),
  ),
  totalPagos: z.number().int(),

  /** Avance por empresa. */
  porEmpresa: z.array(
    z.object({
      empresa: z.string(),
      hechos: z.number().int(),
      total: z.number().int(),
      porcentaje: z.number(),
    }),
  ),
  empresasCargadas: z.number().int(),
  empresasTotales: z.number().int(),

  /** Qué ha pasado, de lo que el pipeline dejó anotado. */
  actividad: z.array(
    z.object({
      texto: z.string(),
      hora: z.string(),
      tono: z.enum(["ok", "warn", "bad", "acc", "neutro"]),
    }),
  ),

  /** Lo que impide cerrar el mes. */
  avisos: z.array(
    z.object({
      titulo: z.string(),
      detalle: z.string(),
      gravedad: z.enum(["error", "aviso"]),
    }),
  ),
});

/**
 * Una fila de la mesa de trabajo: las seis columnas de la lámina 9, ni una más.
 * «Lo demás se ve al abrir cada uno».
 */
export const filaMesaSchema = z.object({
  id: z.string(),
  fecha: z.string(),
  importe: z.number().int(),
  moneda: z.enum(["PEN", "USD"]),
  /** El equivalente en soles, cuando la cuenta es en dólares. */
  importePen: z.number().int().nullable(),
  operacion: z.string().nullable(),
  quePaso: z.string(),
  /** La segunda línea de la descripción: canal, agencia, matiz. */
  detalle: z.string().nullable(),
  /** Lo mismo dicho en castellano, para la vista sencilla. */
  enLlano: z.string(),
  /** Por qué este no se cerró solo, y con qué gravedad. */
  motivo: z.string(),
  motivoTono: z.enum(["bad", "warn", "loan"]),
  /** La boleta del candidato, cuando hay uno solo. */
  boleta: z.string().nullable(),
  boletaExtra: z.number().int(),
  banco: z.string(),
  moneda_grupo: z.string(),
  empresa: z.string().nullable(),
  /** Lo que el cruce propone, si ya corrió. */
  voucherPropuesto: z.string().nullable(),
  voucherNota: z.string().nullable(),
  regla: z.string().nullable(),
  confianza: z.string().nullable(),
  confianzaTono: z.enum(["ok", "warn", "bad", "neutro"]).nullable(),
  /**
   * El score que respalda esa etiqueta, de 0 a 1.
   *
   * La etiqueta se lee de un vistazo; el número permite comparar dos cruces de
   * reglas distintas y ordenar por él. No es una probabilidad — es la suma de
   * las señales que se pudieron comprobar.
   */
  puntaje: z.number().nullable(),
  estadoEtiqueta: z.string(),
  estadoTono: z.enum(["ok", "warn", "bad", "loan", "neutro"]),
});

/** Una cabecera de grupo: «BCP · Soles — 54 movimientos · S/ 318.402,60». */
export const grupoMesaSchema = z.object({
  clave: z.string(),
  banco: z.string(),
  moneda: z.enum(["PEN", "USD"]),
  cuantos: z.number().int(),
  suma: z.number().int(),
});

export const mesaSchema = z.object({
  filas: z.array(filaMesaSchema),
  grupos: z.array(grupoMesaSchema),
  total: z.number().int(),
  conteos: z.object({
    todos: z.number().int(),
    porDecidir: z.number().int(),
    conciliados: z.number().int(),
    prestamos: z.number().int(),
    sinRespaldo: z.number().int(),
  }),
  /**
   * Cuánto suma lo que está por decidir, POR MONEDA.
   *
   * Nunca un total único: sumar 450.000 dólares con 115.000 soles da un número
   * que no significa nada, y al pie de una lista parece que sí.
   */
  sumaPorDecidir: z.object({ PEN: z.number().int(), USD: z.number().int() }),
  /**
   * Con qué se puede filtrar, sacado de lo que HAY y no de una lista fija.
   *
   * Una lista fija se queda vieja en cuanto entra un banco nuevo, y entonces
   * el filtro esconde movimientos sin decirlo.
   */
  opciones: z.object({
    bancos: z.array(z.string()),
    cuentas: z.array(
      z.object({
        id: z.string(),
        banco: z.string(),
        numero: z.string(),
        moneda: z.enum(["PEN", "USD"]),
      }),
    ),
    empresas: z.array(z.object({ id: z.string(), razonSocial: z.string() })),
  }),
});

/**
 * Lo mínimo que la barra superior necesita saber: en qué periodo se trabaja y
 * si está abierto.
 *
 * Va aparte de `inicio` a propósito. La barra se pinta en TODAS las páginas, y
 * `inicio` hace una docena de consultas para armar sus tiles: colgarle la barra
 * significaría pagar ese coste en cada navegación, incluso en pantallas que no
 * enseñan ninguna cifra.
 */
export const contextoSchema = z.object({
  periodo: z.string(),
  estado: z.enum(["abierto", "cerrado"]),
  desde: z.string(),
  hasta: z.string(),
});

/** La ficha de un movimiento: lo que hace falta para decidir sobre él. */
export const fichaSchema = z.object({
  id: z.string(),
  fecha: z.string(),
  importe: z.number().int(),
  moneda: z.enum(["PEN", "USD"]),
  operacion: z.string().nullable(),
  descripcion: z.string(),
  banco: z.string(),
  cuenta: z.string().nullable(),
  empresa: z.string().nullable(),
  estadoEtiqueta: z.string(),
  estadoTono: z.enum(["ok", "warn", "bad", "loan", "neutro"]),
  /**
   * En qué punto del recorrido está, de verdad.
   *
   * La pantalla pinta seis etapas y esto decía sólo dos —«conciliado» o «por
   * decidir»—, así que la barra distinguía la mitad de lo que enseñaba.
   */
  etapa: z.enum([
    "recibido",
    "leido",
    "cruzado",
    "por_decidir",
    "conciliado",
    "cerrado",
  ]),
  antiguedadDias: z.number().int(),

  /**
   * CONTRA QUÉ SE CONCILIÓ ESTE ABONO. Vacío si todavía no se decidió.
   *
   * Esto faltaba, y su ausencia era un fallo grave y silencioso: `ficha` no
   * consultaba `cruce` en ningún momento. La pantalla enseñaba como «el
   * voucher del cliente» el primer elemento de `candidatos`, que era una
   * consulta por importe **sin `orderBy`** — o sea, lo que Postgres devolviera
   * primero. Comprobado sobre cinco movimientos conciliados, cuatro enseñaban
   * una boleta que NO era contra la que se habían conciliado.
   *
   * Es una lista y no un objeto porque un abono puede tener varios cruces: un
   * pago de S/ 2.000 que cubre dos boletas de S/ 1.000 tiene dos.
   */
  conciliado: z.array(
    z.object({
      comprobanteId: z.string(),
      boleta: z.string().nullable(),
      cliente: z.string().nullable(),
      /** La porción de este abono aplicada a esta boleta, no el abono entero. */
      importe: z.number().int(),
      moneda: z.enum(["PEN", "USD"]),
      /** Lo que dice la boleta que emitió Ecoplaza, para poder contrastarlo. */
      totalComprobante: z.number().int().nullable(),
      /** El voucher con el que se ató, si se ató con uno. */
      voucherId: z.string().nullable(),
      recorte: z.string().nullable(),
      importeVoucher: z.number().int().nullable(),
      regla: z.string(),
      confianza: z.string(),
      puntaje: z.number(),
      /** `usuario` o `automatico`: quién lo ató. */
      origen: z.string(),
      /** Quién firmó, cuando lo firmó una persona. */
      quien: z.string().nullable(),
      cuando: z.string().nullable(),
      documento: z
        .object({
          archivoId: z.string(),
          pagina: z.number().int(),
          paginas: z.number().int().nullable(),
        })
        .nullable(),
    }),
  ),

  /**
   * Los vouchers que podrían explicarlo, por importe y moneda.
   *
   * Vienen ordenados por CERCANÍA DE FECHA, que es lo que pidió contabilidad:
   * «los candidatos importa más su fecha que el importe sea igual». Antes esta
   * consulta no tenía `orderBy` ninguno y se cortaba en 12: sobre los 8.046
   * pares (movimiento, voucher) que casan solo por importe, el 76 % está a más
   * de una semana del abono, así que los doce que salían eran una lotería.
   */
  candidatos: z.array(
    z.object({
      id: z.string(),
      operacion: z.string().nullable(),
      fecha: z.string().nullable(),
      importe: z.number().int().nullable(),
      moneda: z.enum(["PEN", "USD"]).nullable(),
      comprobanteId: z.string().nullable(),
      comprobante: z.string().nullable(),
      cliente: z.string().nullable(),
      /**
       * De qué banco salió el papel.
       *
       * Sale de `lectura_vision.banco`. Antes salía de `voucher.banco_destino`,
       * que está escrita en 2 de las 144 filas de voucher: la ficha enseñaba
       * «—» casi siempre teniendo el dato al lado, en otra tabla.
       */
      banco: z.string().nullable(),
      /** Cómo se pagó: yape, plin, transferencia, depósito… */
      medio: z.string().nullable(),
      /** Captura de pantalla o foto tomada. */
      origen: z.string().nullable(),
      /** La hora impresa en el papel, «HH:MM». */
      hora: z.string().nullable(),
      /** Quién pagó, leído de la imagen. No es el beneficiario. */
      pagador: z.string().nullable(),
      /** A qué cuenta entró, tal como se leyó. */
      cuentaDestino: z.string().nullable(),
      /** Esa cuenta resuelta contra el maestro. `null` = no está registrada. */
      cuentaRegistrada: z
        .object({
          banco: z.string(),
          numero: z.string(),
          moneda: z.enum(["PEN", "USD"]),
          razonSocial: z.string().nullable(),
        })
        .nullable(),
      /** Cuántos dígitos separan su operación de la del extracto. */
      distancia: z.number().int().nullable(),
      /**
       * Días entre la fecha del voucher y la del abono. Negativo si el voucher
       * es posterior, que pasa en 761 de los 8.046 pares y casi siempre
       * significa que no es.
       *
       * `null` cuando el voucher no trae fecha — 513 pares.
       */
      diasDeDiferencia: z.number().int().nullable(),
      /**
       * Si el MOTOR lo consideró candidato de este abono.
       *
       * El motor exige, además del importe, que la fecha caiga en su ventana de
       * 0 a 7 días. Los que no lo cumplen se siguen enseñando —esta pantalla es
       * donde se busca justo lo que el motor descartó— pero dichos como lo que
       * son, y no mezclados con los que sí propuso.
       */
      deElMotor: z.boolean(),
      /** Cómo se comparan los dos números, dicho en palabras. */
      comparacion: z.enum([
        "identico",
        "mismo_final",
        "difieren",
        "no_comparable",
      ]),
      recorte: z.string().nullable(),
      /**
       * Dónde está la boleta emitida, para poder abrirla aquí también.
       *
       * Es la misma pregunta que la revisión guiada —«¿este abono es el pago
       * de esa boleta?»— y a esta pantalla le faltaba el otro papel.
       */
      documento: z
        .object({
          archivoId: z.string(),
          pagina: z.number().int(),
          /** Cuántas hojas trae el expediente, para poder verlo entero. */
          paginas: z.number().int().nullable(),
        })
        .nullable(),
      /** Si esa boleta ya la cobró otro abono. */
      yaTomada: z.boolean(),
      /** Si es ESTE abono el que la cobró: el candidato que se acabó eligiendo. */
      esElConciliado: z.boolean(),
    }),
  ),
  /**
   * Cuántos candidatos hay en total, para poder decir «y N más» sin mentir.
   *
   * La lista se corta en 12. Antes se cortaba sin orden y sin decirlo, así que
   * en 10 de los 192 movimientos conciliados la boleta buena podía no salir.
   */
  candidatosTotales: z.number().int(),
  /** El texto que el lector sacó del voucher elegido, tal cual. */
  textoVoucher: z.string().nullable(),
});

/** El tablero de documentos: en qué punto de la lectura está cada archivo. */
export const documentosSchema = z.object({
  archivos: z.number().int(),
  /**
   * CUÁNTAS IMÁGENES QUEDAN POR LEER, y qué costaría leerlas.
   *
   * El tablero decía «376 leídas» y callaba que había 928 sin leer: el pipeline
   * ya pasa el lector óptico —local y gratis— pero no lo resuelve todo, y lo
   * que se queda sin importe necesita el modelo, que se cobra por llamada.
   *
   * Sin este bloque, quien sube el mes ve todo en verde, le da a cruzar y le
   * salen cero cruces sin que nada diga que falta un paso.
   */
  lectura: z
    .object({
      imagenes: z.number().int(),
      /** Ni el óptico las miró: si esto no es cero, algo falló al subir. */
      sinLeer: z.number().int(),
      porOptico: z.number().int(),
      porModelo: z.number().int(),
      /**
       * Las que el óptico miró y no pudo resolver: son vouchers y siguen sin
       * importe. Es lo que el modelo arreglaría, y por lo que se paga.
       */
      paraElModelo: z.number().int(),
      /**
       * Las que la lectura NO llamó voucher y aun así traen señas de pago:
       * importe, banco o número de operación, dos de las tres.
       *
       * Se cuentan aparte porque su daño es silencioso: el cruce exige
       * `clase = 'voucher'`, así que ese pago no se concilia aunque el importe
       * esté bien leído. Medido en el periodo cargado antes de arreglarlo:
       * 41 imágenes en 42 expedientes, 29 con importe y **ninguna** cruzada.
       */
      malClasificadas: z.number().int(),
      /** Lo que costaría leerlas, en dólares. */
      costoEstimado: z.number(),
      /** Lo que ya se lleva gastado. */
      costoGastado: z.number(),
    })
    // `null` en la mitad de extractos: lo que cuenta son imágenes de voucher, y
    // un estado de cuenta no tiene ninguna. Salía idéntica en las dos
    // pantallas —con su coste y su botón de pagar al modelo— sin significar
    // nada en una de ellas.
    .nullable(),
  vouchersEncontrados: z.number().int(),
  imagenesDistintas: z.number().int(),
  repetidosDescartados: z.number().int(),
  noSePudieronLeer: z.number().int(),
  /**
   * Lo que solo importa de los estados de cuenta. `null` en la otra mitad.
   *
   * Los cinco contadores de arriba son de vouchers —imágenes distintas,
   * repetidos descartados— y para un extracto no significan nada.
   */
  extractos: z
    .object({
      /** De cuántas cuentas del maestro hay extracto cargado. */
      cuentasCubiertas: z.number().int(),
      cuentasTotales: z.number().int(),
      /** Cuántos cuadran contra los totales que declara el banco. */
      cuadran: z.number().int(),
      /** Cuántos traen algún hallazgo de la validación. */
      conHallazgos: z.number().int(),
      /** Los meses que cubren los extractos cargados. */
      meses: z.array(z.string()),
    })
    .nullable(),

  columnas: z.array(
    z.object({
      clave: z.string(),
      titulo: z.string(),
      cuantos: z.number().int(),
      tono: z.enum(["ok", "warn", "bad", "acc", "neutro"]),
      /** Lo que explica la columna cuando está vacía o cuesta entenderla. */
      nota: z.string().nullable(),
      tarjetas: z.array(
        z.object({
          id: z.string(),
          titulo: z.string(),
          subtitulo: z.string().nullable(),
          etiqueta: z.string().nullable(),
          detalle: z.string().nullable(),
        }),
      ),
    }),
  ),
});

/**
 * El expediente de un archivo: qué se hizo con él y qué salió.
 *
 * Es la respuesta a la pregunta de contabilidad —«¿esto se leyó bien?»— y hasta
 * ahora no tenía dónde responderse: el tablero enseñaba en qué paso va cada
 * archivo y sus tarjetas no se podían abrir. Todo lo que hace falta ya estaba
 * guardado en `paso.detalle`; lo que faltaba era enseñarlo.
 */
export const fichaArchivoSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  clase: z.enum(["comprobante", "estado_cuenta"]),
  bytes: z.number().int(),
  sha256: z.string(),
  /**
   * CUÁNTAS HOJAS TIENE EL PDF, para poder verlo entero.
   *
   * La tabla lo sabe desde siempre y sin un solo nulo —745 hojas en 150
   * expedientes, 5 de media—, y no salía en ninguna respuesta: la pantalla
   * enseñaba UNA hoja, la del comprobante, de las cinco que trae el
   * expediente. Con esto el visor sabe hasta dónde puede pasar página.
   */
  paginas: z.number().int().nullable(),
  subidoEl: z.string(),

  /** Las seis etapas, siempre las seis, estén o no. */
  pasos: z.array(
    z.object({
      etapa: z.string(),
      estado: z.enum(["pendiente", "en_curso", "hecho", "fallado", "omitido"]),
      intentos: z.number().int(),
      /** Cuánto tardó, en segundos. `null` si no llegó a terminar. */
      duro: z.number().nullable(),
      /** Lo que dejó dicho esa etapa, en frases. */
      dice: z.array(z.string()),
      error: z.string().nullable(),
    }),
  ),

  /** Si es un estado de cuenta: de quién es y qué trajo. */
  extracto: z
    .object({
      banco: z.string().nullable(),
      origenBanco: z.string().nullable(),
      moneda: z.string().nullable(),
      empresa: z.string().nullable(),
      cuenta: z.string().nullable(),
      movimientos: z.number().int(),
      abonos: z.number().int(),
      cargos: z.number().int(),
      valido: z.boolean().nullable(),
      hallazgos: z.array(
        z.object({
          codigo: z.string(),
          gravedad: z.string(),
          mensaje: z.string(),
        }),
      ),
    })
    .nullable(),

  /** Si es un expediente: qué comprobante salió y cuántas imágenes. */
  expediente: z
    .object({
      comprobanteId: z.string().nullable(),
      serieNumero: z.string().nullable(),
      cliente: z.string().nullable(),
      total: z.number().int().nullable(),
      moneda: z.string().nullable(),
      /** En qué página estaba la boleta, para poder abrirla. */
      pagina: z.number().int().nullable(),
      origenLectura: z.string().nullable(),
      /**
       * Los OTROS comprobantes del mismo expediente, si los hay.
       *
       * El caso corriente son tres papeles de la misma venta: la boleta que
       * salió mal, la nota de crédito que la anula, y la que la reemplaza.
       * Cinco de los 150 expedientes de enero vienen así, y hasta que el lector
       * aprendió a separarlos la pantalla enseñaba uno como si fuera todo lo
       * que había dentro.
       */
      otrosComprobantes: z.array(
        z.object({
          serieNumero: z.string().nullable(),
          tipo: z.string(),
          moneda: z.string(),
          total: z.number().int().nullable(),
          estado: z.string(),
          /** Qué comprobante anula, si es una nota de crédito. */
          anulaA: z.string().nullable(),
          pagina: z.number().int().nullable(),
        }),
      ),
      /**
       * El cliente del NOMBRE del fichero, cuando no es el del papel.
       *
       * `null` cuando coinciden, que es lo normal. Cuando no, uno de los dos
       * está mal y hay que mirarlo: la pantalla los enseñaba en verde, uno al
       * lado del otro, sin decir que se contradicen.
       */
      clienteDelNombre: z.string().nullable(),

      /**
       * LA FICHA DEL CLIENTE, la hoja de verdad y no sus recortes.
       *
       * La pantalla enseñaba, bajo el rótulo «la ficha del local», los trozos
       * de imagen que el clasificador etiquetó como `ficha` — y en un
       * expediente real eso eran cuatro copias de la NOTA legal del pie, la
       * que empieza «las separaciones tienen una vigencia». Esa etiqueta está
       * bien puesta y hace falta: impide que el cronograma de cuotas se lea
       * como un pago. Lo que estaba mal era presentarla como la ficha.
       *
       * La ficha ya se lee entera y se guarda en su tabla, con la página en la
       * que está. Es lo que hay que enseñar, y desde ahí se abre la hoja.
       */
      ficha: z
        .object({
          pagina: z.number().int().nullable(),
          nombre: z.string().nullable(),
          documento: z.string().nullable(),
          telefonos: z.array(z.string()),
          email: z.string().nullable(),
          direccion: z.string().nullable(),
          distrito: z.string().nullable(),
          ocupacion: z.string().nullable(),
        })
        .nullable(),

      imagenes: z.array(
        z.object({
          recorte: z.string(),
          /**
           * Qué pago de esa imagen es. Una foto puede traer dos vouchers, y
           * enseñar solo el primero deja la mitad del dinero fuera de la vista.
           */
          pagoN: z.number().int(),
          /** Cuántos pagos vio la lectura en la imagen. */
          dePagos: z.number().int().nullable(),
          pagina: z.number().int().nullable(),
          clase: z.string().nullable(),
          medio: z.string().nullable(),
          importe: z.number().int().nullable(),
          operacion: z.string().nullable(),
          /** `true` si una persona ya la verificó. */
          verificada: z.boolean(),

          /**
           * TODO lo que se pudo sacar de la imagen.
           *
           * La ficha del archivo enseñaba la miniatura, la clase y poco más, y
           * el dato leído se quedaba en la base. Se vio con un expediente en el
           * que el óptico clasificó un voucher como «ficha» y AUN ASÍ le sacó
           * la moneda, el banco y el número de operación: la pantalla decía
           * «ficha» y no había forma de ver que dentro había un pago leído.
           *
           * Va aquí y no en otra consulta porque ya está a mano: lo escribe la
           * misma lectura que llena `clase`.
           */
          moneda: z.string().nullable(),
          fechaPago: z.string().nullable(),
          hora: z.string().nullable(),
          banco: z.string().nullable(),
          cuentaDestino: z.string().nullable(),
          pagador: z.string().nullable(),
          beneficiario: z.string().nullable(),
          /** Quién la leyó: el óptico local o un modelo de pago. */
          leidaPor: z.string().nullable(),
          /** Cómo llegó: captura de pantalla o foto de papel. */
          tipoDeImagen: z.string().nullable(),
          /**
           * La cuenta del GRUPO a la que fue el dinero, si se pudo resolver.
           *
           * Es el dato que contesta «¿este pago entró en una cuenta nuestra, y
           * de qué razón social?». Sin él, la cuenta de destino es un número
           * suelto que no dice si el dinero llegó a casa.
           */
          cuentaDelGrupo: z
            .object({
              banco: z.string(),
              numero: z.string(),
              moneda: z.string(),
              razonSocial: z.string().nullable(),
            })
            .nullable(),
          /**
           * La clase dice una cosa y los datos leídos dicen otra.
           *
           * Es el caso que lo destapó: `clase = ficha` con banco, moneda y
           * número de operación dentro. No se corrige solo —reclasificar por
           * heurística puede empeorarlo— pero deja de pasar desapercibido.
           */
          claseDudosa: z.boolean(),
          /**
           * El movimiento del banco al que está atado ESTE pago.
           *
           * Faltaba: la ficha enseñaba los vouchers y no si alguno estaba ya
           * conciliado, así que no había forma de ver de un vistazo cuánto
           * queda del expediente por atar.
           */
          enlace: z
            .object({
              movimientoId: z.string(),
              fecha: z.string(),
              banco: z.string(),
              importe: z.number().int(),
              moneda: z.string(),
              operacion: z.string().nullable(),
            })
            .nullable(),
          /** «Ya lo miré y no está en ningún extracto cargado». */
          sinAbono: z.boolean(),
        }),
      ),
    })
    .nullable(),

  /** Qué se puede hacer con él ahora mismo. */
  sePuedeReintentar: z.boolean(),
});

/**
 * La vista previa de lo que se va a entregar a CONCAR.
 *
 * Las líneas van tal como se van a escribir en el Excel, no como se guardan:
 * el importe en unidades y no en céntimos, la cuenta como texto. Lo que se ve
 * aquí es exactamente lo que va a ir en el archivo.
 */
export const asientosSchema = z.object({
  periodo: z.string(),
  /** Cada asiento son dos líneas que tienen que sumar lo mismo. */
  asientos: z.number(),
  lineas: z.number(),
  /**
   * El cuadre, SEPARADO POR MONEDA.
   *
   * Sumar soles y dólares en la misma cifra da un total que no existe. Cada
   * asiento cuadra consigo mismo en su propia moneda, y así es como hay que
   * enseñarlo. En céntimos, para comprobarlo sin coma flotante.
   */
  porMoneda: z.array(
    z.object({
      moneda: z.string(),
      asientos: z.number(),
      debe: z.number(),
      haber: z.number(),
      cuadra: z.boolean(),
    }),
  ),
  cuadra: z.boolean(),
  errores: z.number(),
  avisos: z.number(),
  /**
   * Los problemas agrupados por código: uno por tipo, con cuántos son y unos
   * cuantos comprobantes de ejemplo. Doscientas líneas iguales no informan más
   * que una que diga cuántas veces pasa.
   */
  problemas: z.array(
    z.object({
      nivel: z.enum(["error", "aviso"]),
      codigo: z.string(),
      mensaje: z.string(),
      cuantos: z.number(),
      ejemplos: z.array(z.string()),
    }),
  ),
  /** Numeraciones cuyo punto de partida nadie confirmó. */
  arranqueSinConfirmar: z.number(),
  /** Del primero al último número de comprobante que se va a consumir. */
  desde: z.string().nullable(),
  hasta: z.string().nullable(),
  /** Las primeras líneas, tal como van a salir en el Excel. */
  muestra: z.array(
    z.object({
      subDiario: z.string(),
      numeroComprobante: z.string(),
      fechaComprobante: z.string(),
      codigoMoneda: z.string(),
      cuentaContable: z.string(),
      codigoAnexo: z.string().nullable(),
      debeHaber: z.string(),
      importe: z.number(),
      tipoDocumento: z.string().nullable(),
      numeroDocumento: z.string().nullable(),
      codigoArea: z.string().nullable(),
      glosaPrincipal: z.string(),
      glosaDetalle: z.string(),
    }),
  ),
  /** Lo que ya se entregó de este periodo. */
  entregas: z.array(
    z.object({
      subDiario: z.string(),
      archivo: z.string(),
      usuario: z.string(),
      lineas: z.number(),
      desde: z.string().nullable(),
      hasta: z.string().nullable(),
      cuando: z.string(),
      forzada: z.boolean(),
    }),
  ),
});

/** El cierre del periodo: qué hay, qué sale y qué falta para cerrar. */
export const cierreSchema = z.object({
  periodo: z.string(),
  estado: z.enum(["abierto", "cerrado"]),
  /** Una fila por empresa; las columnas son los estados. */
  filas: z.array(
    z.object({
      empresa: z.string(),
      conciliado: z.number().int(),
      porDecidir: z.number().int(),
      prestamo: z.number().int(),
      sinRespaldo: z.number().int(),
      total: z.number().int(),
    }),
  ),
  totales: z.object({
    conciliado: z.number().int(),
    porDecidir: z.number().int(),
    prestamo: z.number().int(),
    sinRespaldo: z.number().int(),
    total: z.number().int(),
  }),
  /** La moneda de la tabla: no se mezclan sin tipo de cambio. */
  moneda: z.enum(["PEN", "USD"]),
  cerradoSinIntervencion: z.number(),
  movimientos: z.number().int(),
  /** Cuánto resolvió cada regla del cruce. */
  reglas: z.array(
    z.object({ regla: z.string(), que: z.string(), cuantos: z.number().int() }),
  ),
  /** Lo que hace falta antes de poder cerrar. */
  requisitos: z.array(
    z.object({ cumple: z.boolean(), titulo: z.string(), detalle: z.string() }),
  ),
});

/** Un caso de la revisión guiada: una pregunta y dos lados que comparar. */
export const revisionSchema = z.object({
  indice: z.number().int(),
  /** Cuántos hay en la banda que se está mirando. */
  total: z.number().int(),
  /**
   * Cuántos hay en cada montón, para pintar las pestañas con su número.
   *
   * Van SIEMPRE los cuatro, aunque alguno esté en cero: una banda que
   * desaparece cuando se vacía hace creer que no existe.
   */
  bandas: z.object({
    facil: z.number().int(),
    normal: z.number().int(),
    dificil: z.number().int(),
    sinCandidato: z.number().int(),
  }),
  /** `null` cuando ya no queda ninguno. */
  caso: z
    .object({
      id: z.string(),
      pregunta: z.string(),
      porQue: z.string(),
      banco: z.object({
        importe: z.number().int(),
        moneda: z.enum(["PEN", "USD"]),
        cuando: z.string(),
        donde: z.string(),
        operacion: z.string().nullable(),
        concepto: z.string(),
        /**
         * Si el nombre que el banco escribió en la glosa es el del cliente de
         * la boleta que se está proponiendo.
         *
         * Es el dato que resuelve la mirada en un segundo cuando no hay número
         * de operación —Yape y Plin no lo traen— y es casi toda la ambigüedad
         * que queda. `null` cuando el banco no escribe nombres, que es lo que
         * hace el BCP: su glosa es todo códigos.
         *
         * Que NO coincida no descarta nada: muchas veces deposita un familiar.
         * Por eso se enseña el hecho y no un veredicto.
         */
        nombreEnLaGlosa: z
          .object({
            /** Las palabras que casaron, para poder verlas. */
            casados: z.array(z.string()),
            /** `true` si llega al umbral con el que el motor se fiaría. */
            fuerte: z.boolean(),
          })
          .nullable(),
        /**
         * La hora que el banco escribió en la glosa, «HH:MM».
         *
         * Solo el BCP la imprime —2.158 de sus 2.427 movimientos— y es
         * justamente el banco que no escribe ningún nombre. Entre cuatro
         * abonos del mismo importe, el mismo día y del mismo cliente, es lo
         * único que los distingue.
         */
        hora: z.string().nullable(),
      }),
      /** Contra qué boleta se pregunta. Es lo que viaja al confirmar. */
      comprobanteId: z.string().nullable(),
      voucher: z
        .object({
          importe: z.number().int().nullable(),
          moneda: z.enum(["PEN", "USD"]).nullable(),
          cuando: z.string().nullable(),
          boleta: z.string().nullable(),
          cliente: z.string().nullable(),
          operacion: z.string().nullable(),
          /**
           * Cómo se pagó: Yape, Plin, transferencia, depósito…
           *
           * No es decoración. Un Yape no trae número de operación —lo único que
           * lo identifica es el nombre de a quién se pagó— y una transferencia
           * interbancaria tarda hasta dos días en acreditarse. Quien revisa
           * necesita saber cuál de las dos está mirando antes de juzgar que el
           * número no coincide o que la fecha no cuadra.
           */
          medio: z.string().nullable(),
          /**
           * Cómo llegó la imagen: «Captura de pantalla» o «Foto tomada».
           *
           * Tampoco es decoración. Una captura se lee casi siempre bien; una
           * foto de papel arrugado es donde el lector falla, y saber cuál se
           * está mirando explica por qué un dato viene dudoso.
           *
           * Sale de lo plano del color de la imagen. El EXIF y el formato no
           * sirven: empotrar la imagen en el PDF los borra —289 de 294 sin
           * EXIF, 294 de 294 como JPEG—.
           */
          origen: z.string().nullable(),
          /** La hora impresa en el voucher, «HH:MM». */
          hora: z.string().nullable(),
          /**
           * Quién PAGÓ, leído de la imagen. No es el beneficiario.
           *
           * En un Yape o un Plin es lo único que dice de quién viene el
           * dinero, y casi nunca es el cliente de la boleta: suele pagar un
           * familiar. Se enseña aunque ninguna regla lo use, porque es el dato
           * que le falta a quien tiene que decidir entre cuatro abonos
           * iguales.
           */
          pagador: z.string().nullable(),
          /**
           * DE QUÉ BANCO SALIÓ EL PAPEL. No confundir con `medio`.
           *
           * Son dos ejes distintos y contabilidad los pidió juntos —«banco del
           * voucher (yape, plin, bcp, bbva)»—: `medio` dice CÓMO se pagó
           * —yape, plin, transferencia, depósito— y `banco` dice DE DÓNDE
           * salió. Un Yape se hace desde el BCP, así que son dos chips y no
           * uno.
           *
           * Sale de `lectura_vision.banco`, que es de la IMAGEN. La columna
           * `voucher.banco_destino` existe y está casi vacía —2 filas de 144
           * aquí— y era la que leía la ficha.
           */
          banco: z.string().nullable(),
          /**
           * A QUÉ CUENTA ENTRÓ EL DINERO, tal como lo leyó el modelo.
           *
           * Es lo que más desempata cuando hay cuatro abonos iguales, y se
           * estaba tirando antes de llegar a la pantalla.
           */
          cuentaDestino: z.string().nullable(),
          /**
           * Esa cuenta, resuelta contra el maestro del grupo.
           *
           * `null` significa **que no está registrada**, y es un dato en sí
           * mismo: contabilidad pidió ver «si está registrado o no está
           * registrado en la empresa». De 92 cuentas leídas, 65 resuelven y 27
           * no. Una cuenta que no es del grupo es un pago que quizá no era
           * para nosotros.
           *
           * Ojo al leerlo: `null` aquí no distingue «no está registrada» de
           * «no se pudo leer la cuenta». Para eso está `cuentaDestino`: si
           * viene con número y esto viene `null`, no está registrada.
           */
          cuentaRegistrada: z
            .object({
              banco: z.string(),
              numero: z.string(),
              moneda: z.enum(["PEN", "USD"]),
              razonSocial: z.string().nullable(),
            })
            .nullable(),
          comparacion: z.enum([
            "identico",
            "mismo_final",
            "difieren",
            "no_comparable",
          ]),
          /**
           * Si la hora del voucher es la que el banco asentó.
           *
           * `null` cuando falta alguna de las dos — que es lo normal fuera del
           * BCP. Se enseña el hecho y no un veredicto, igual que con el nombre
           * en la glosa.
           */
          horaCuadra: z.boolean().nullable(),
        })
        .nullable(),
      otrosCandidatos: z.number().int(),
      /** Las demás boletas que podrían ser, para poder elegir otra. */
      candidatos: z.array(
        z.object({
          comprobanteId: z.string().nullable(),
          boleta: z.string().nullable(),
          cliente: z.string().nullable(),
          operacion: z.string().nullable(),
          cuando: z.string().nullable(),
          medio: z.string().nullable(),
          /**
           * Cuánta evidencia hay a favor de esta boleta, de 0 a 1.
           *
           * Vienen ordenados por él. Sin este número la pantalla decía «el
           * mejor primero» y el orden real era el alfabético de la serie.
           */
          puntaje: z.number(),
          /**
           * Si esta boleta ya está cobrada por otro abono.
           *
           * Es lo que pidió contabilidad poder ver «en algún lado»: que una
           * boleta aparezca como candidata no significa que esté libre, y
           * elegirla sería cobrarla dos veces.
           */
          yaTomada: z.boolean(),
        }),
      ),
      /** La clave de la imagen en el almacén, para poder mirarla. */
      recorte: z.string().nullable(),
      /**
       * Los datos del cliente que trae la ficha de inscripción.
       *
       * Es la tercera hoja del expediente —218 de los 273 la traen— y hasta
       * ahora se tiraba entera. La pidió contabilidad, y el dato que más pesa
       * es el TELÉFONO: un Yape y un Plin no llevan número de operación ni
       * cuenta de destino, así que el móvil del que envía es lo único que
       * identifica el pago.
       *
       * `null` cuando el expediente no trae ficha, que son 55 de 273.
       */
      ficha: z
        .object({
          nombre: z.string(),
          /** DNI de ocho dígitos, o RUC de once si el cliente es una empresa. */
          documento: z.string().nullable(),
          /** En plural: 62 de las 218 fichas traen dos móviles. */
          telefonos: z.array(z.string()),
          distrito: z.string().nullable(),
          ocupacion: z.string().nullable(),
          /** Para abrir la hoja, igual que con la boleta. */
          archivoId: z.string().nullable(),
          pagina: z.number().int().nullable(),
          /**
           * Si quien firma la boleta y quien figura en la ficha son la misma
           * persona. Cuando no lo son es un dato que cambia la decisión: en
           * estos expedientes suele pagar un familiar.
           */
          cuadraConLaBoleta: z.boolean(),
        })
        .nullable(),
      /**
       * Dónde está la boleta o factura que emitió Ecoplaza.
       *
       * La pregunta de esta pantalla es «¿este abono es el pago de esa
       * boleta?», y de la boleta solo se enseñaba su número de serie. Con esto
       * se puede abrir la hoja: el documento emitido, con los datos del
       * cliente, que en el expediente va antes del voucher.
       *
       * `null` cuando el expediente es todo escaneo y no se encontró la
       * página — entonces no hay nada que abrir y no se ofrece.
       */
      documento: z
        .object({
          archivoId: z.string(),
          pagina: z.number().int(),
          /** Cuántas hojas trae el expediente, para poder verlo entero. */
          paginas: z.number().int().nullable(),
          /** Lo que dice el papel, para poder contrastarlo sin abrirlo. */
          total: z.number().int().nullable(),
          moneda: z.enum(["PEN", "USD"]).nullable(),
        })
        .nullable(),
      /**
       * LOS TRES IMPORTES, comparados de una vez.
       *
       * Son tres —lo que entró al banco, lo que dice el voucher y lo que dice
       * la boleta— y se comparaban de dos en dos, y solo una pareja: la
       * pantalla enfrentaba la boleta con el banco y el voucher no entraba.
       *
       * De 136 filas de voucher con importe y boleta, 59 cuadran y 77 no. No
       * todas son un error —47 son pagos por menos que la boleta, normales si
       * se cobra a plazos— así que esto dice QUÉ SITUACIÓN ES y no si está mal.
       */
      cuadre: z.object({
        clase: z.enum([
          "cuadra",
          "sin_voucher",
          "sin_total",
          "moneda_distinta",
          "pago_agrupado",
          "pago_parcial",
          "no_cuadra",
        ]),
        dice: z.string(),
        diferencia: z.number().int(),
        /** `true` cuando conviene mirarlo antes de cerrar. */
        avisa: z.boolean(),
      }),

      /**
       * EL COTEJO: dato por dato, qué coincide y qué no.
       *
       * Las dos columnas —lo que dice el banco, lo que dice el voucher— ya se
       * enseñaban, y la comparación quedaba en los ojos de quien revisa. Con
       * una glosa de sesenta caracteres y dos números de operación de siete
       * dígitos, eso es pedirle que haga de lector óptico: en el primer caso
       * real que se miró, el banco decía `5394085` y el voucher `5394081` —el
       * último dígito— y el resumen decía «dicen lo mismo».
       *
       * Van las SEIS filas siempre, en el orden que pidió contabilidad y con
       * las que no se pudieron comprobar incluidas: esconder una porque falta
       * el dato la vuelve indistinguible de una que cuadró.
       */
      cotejo: z.array(
        z.object({
          clave: z.enum([
            "fecha",
            "operacion",
            "monto",
            "banco",
            "cliente",
            "boleta",
          ]),
          titulo: z.string(),
          veredicto: z.enum(["coincide", "difiere", "no_se_puede"]),
          dice: z.string(),
          enElBanco: z.string().nullable(),
          enElVoucher: z.string().nullable(),
        }),
      ),
    })
    .nullable(),
});

/**
 * Lo que una persona decide sobre un movimiento.
 *
 * `si` lo concilia contra la boleta propuesta, `no` descarta ese candidato y
 * `no_se` lo deja anotado para contabilidad. Las tres quedan firmadas: sin
 * saber quién dijo qué y cuándo, un cierre no se puede auditar, que es la
 * mitad de para lo que existe este módulo.
 */
export const decisionSchema = z.object({
  movimientoId: z.string(),
  /** Contra qué boleta. Obligatorio para `si`. */
  comprobanteId: z.string().nullish(),
  respuesta: z.enum(["si", "no", "no_se"]),
  nota: z.string().max(500).nullish(),
});

/**
 * Lo que una persona corrige de lo que leyó la máquina.
 *
 * Va por IMAGEN (`recorte`) y no por fila de voucher: la misma imagen suele
 * estar adjunta a varios expedientes, y corregirla una vez vale para todos. Un
 * voucher que aparece dieciocho veces no son dieciocho pagos.
 *
 * Es la vía de escape que el sistema anterior tenía y este había perdido:
 * cuando el modelo no puede leer una imagen, alguien escribe lo que dice y el
 * cruce se cierra igual. Sin esto, ese caso se queda en «no sabría decir» y no
 * avanza nunca — y de 1.777 imágenes solo 227 traen número de operación leído,
 * así que no es un caso raro.
 */
export const correccionSchema = z.object({
  /** La imagen que se está corrigiendo. */
  recorte: z.string().min(1),
  veredicto: z.enum(["correcto", "corregido", "no_es_voucher", "ilegible"]),
  /** Solo para `corregido`: lo que de verdad dice el papel. */
  fecha: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha va como 2026-01-31")
    .nullish(),
  operacion: z.string().max(40).nullish(),
  /** En céntimos, como todo el dinero de este sistema. */
  importe: z.number().int().min(0).nullish(),
  moneda: z.enum(["PEN", "USD"]).nullish(),
  nota: z.string().max(500).nullish(),
});

export const decisionHechaSchema = z.object({
  ok: z.literal(true),
  movimientoId: z.string(),
  estado: z.string(),
  /** Para poder ofrecer «deshacer» sin recargar nada. */
  sePuedeDeshacer: z.boolean(),
  dice: z.string(),
});

export const seguimientoSchema = z.object({
  entradas: z.array(
    z.object({
      id: z.string(),
      momento: z.string(),
      usuario: z.string().nullable(),
      accion: z.string(),
      dice: z.string(),
    }),
  ),
});

/**
 * Los expedientes del mes, con lo que le falta a cada uno.
 *
 * Es lo mismo que ya calcula la revisión de un lote, pero para el periodo
 * entero y no para una subida. Hasta ahora ese cuadro solo se alcanzaba desde
 * el enlace que sale UNA vez, justo después de subir: al día siguiente no había
 * forma de volver a él, y la pantalla de documentos contestaba con contadores
 * de imágenes —vouchers encontrados, repetidos descartados— que son
 * contabilidad de la máquina, no la pregunta del área.
 */
export const expedientesSchema = z.object({
  periodo: z.string(),
  /**
   * Los meses que existen, con su id.
   *
   * El id viaja porque desde esta pantalla se mueven expedientes de mes, y el
   * nombre `AAAA-MM` no es la llave: dos periodos pueden llamarse igual si
   * alguien los crea a la vez, y mover por nombre repartiría el lote.
   */
  periodos: z.array(z.object({ id: z.string(), nombre: z.string() })),
  archivos: z.number().int(),
  completos: z.number().int(),
  incompletos: z.number().int(),
  enCurso: z.number().int(),
  fallados: z.number().int(),
  /**
   * Cuántos no cuadran consigo mismos.
   *
   * El fichero dice una persona y el papel otra, o dentro hay más de un
   * comprobante. Pueden estar completos y aun así pedir que alguien los mire.
   */
  discrepan: z.number().int(),
  /** Cuántos pasan el filtro pedido. Es lo que pagina. */
  filtradas: z.number().int(),
  expedientes: z.array(
    z.object({
      archivoId: z.string(),
      nombre: z.string(),
      estado: z.string(),
      serieNumero: z.string().nullable(),
      cliente: z.string().nullable(),
      /**
       * La razón social del grupo por la que se agrupa la lista.
       *
       * La declarada al subir manda; si el expediente entró antes de que
       * existiera el formulario, vale la que dice el papel.
       */
      razonSocial: z.string().nullable(),
      razonSocialDeclarada: z.string().nullable(),
      razonSocialDelPapel: z.string().nullable(),
      /** Se declaró una razón social y el papel dice otra. */
      discrepaRazonSocial: z.boolean(),
      total: z.number().int().nullable(),
      moneda: z.enum(["PEN", "USD"]).nullable(),
      paginas: z.number().int().nullable(),
      tieneComprobante: z.boolean(),
      tieneFicha: z.boolean(),
      vouchers: z.number().int(),
      completo: z.boolean(),
      falta: z.string().nullable(),
      /**
       * El cliente del NOMBRE del fichero, cuando no es el del papel.
       *
       * `null` cuando coinciden, que es lo normal. Cuando no, uno de los dos
       * está mal: o el fichero se nombró con el cliente de otro, o dentro hay
       * el expediente de otra persona. Las dos cosas hay que mirarlas, y hasta
       * ahora ninguna pantalla las nombraba.
       */
      clienteDelNombre: z.string().nullable(),
      /**
       * Cuántos comprobantes trae el expediente. Casi siempre uno.
       *
       * Tres es el caso de la venta corregida —boleta mala, nota de crédito y
       * boleta buena— y se dice porque cambia lo que hay que cobrar: la nota
       * resta.
       */
      comprobantes: z.number().int(),
      /**
       * Tiene al menos un cruce contra un abono del banco.
       *
       * Se dice en la lista porque es lo único que impide moverlo de mes o
       * borrarlo: enterarse al pulsar, con la mitad del lote rechazada, es
       * peor que verlo antes de elegir.
       */
      conciliado: z.boolean(),
    }),
  ),
});

/**
 * Un estado de cuenta YA CARGADO, con sus movimientos uno por uno.
 *
 * Es la pantalla que faltaba: la ficha del archivo resumía banco, cuenta y
 * totales, y no listaba un solo movimiento. Para mirar una línea concreta —la
 * que no cuadra contra el PDF del banco— había que irse a la mesa de
 * movimientos del mes entero y filtrar a mano.
 *
 * Los movimientos van PAGINADOS: un extracto de 158 líneas cabe entero, pero
 * los hay de más de mil, y el orden es total —página, línea, id— porque paginar
 * por `offset` sobre un orden que empata pierde filas, que es el fallo que este
 * repositorio ya cazó en dos pantallas.
 */
export const extractoSchema = z.object({
  archivoId: z.string(),
  nombre: z.string(),
  periodo: z.string(),
  /** El periodo está cerrado: no se edita nada. */
  periodoCerrado: z.boolean(),
  /**
   * Cuántas hojas tiene el PDF, para poder pasarlas al lado de la tabla.
   *
   * Las páginas se piden DIBUJADAS a `/pagina/:archivo/:n` y no se sirve el PDF
   * original: ese endpoint tacha los RUC del grupo al dibujar, y servir el
   * archivo tal cual salta la tachadura.
   */
  paginas: z.number().int().nullable(),
  /** Si quien mira puede borrar este extracto. Hace falta rol de jefe. */
  puedeEliminar: z.boolean(),
  banco: z.string().nullable(),
  moneda: z.enum(["PEN", "USD"]).nullable(),
  cuenta: z.string().nullable(),
  cuentaId: z.string().nullable(),
  empresa: z.string().nullable(),
  ruc: z.string().nullable(),
  desde: z.string().nullable(),
  hasta: z.string().nullable(),
  /** Lo que declara el banco contra lo que sale de sumar las líneas. */
  cuadre: cuadreSchema.nullable(),
  hallazgos: z.array(hallazgoSchema),
  /** Cuántos movimientos tiene el extracto entero, no la página. */
  total: z.number().int(),
  /** Cuántos de ellos están cruzados contra una boleta. */
  conciliados: z.number().int(),
  /** Cuántos están fuera de la conciliación por decisión de alguien. */
  noConciliables: z.number().int(),
  /** Cuántos pasan el filtro que se pidió. Es lo que pagina. */
  filtradas: z.number().int(),
  movimientos: z.array(
    z.object({
      id: z.string(),
      pagina: z.number().int().nullable(),
      linea: z.number().int().nullable(),
      /** El número que ocupa en el extracto entero, para poder nombrarlo. */
      orden: z.number().int(),
      fechaOper: z.string(),
      fechaValor: z.string().nullable(),
      descripcion: z.string().nullable(),
      referencia: z.string().nullable(),
      nroOperacion: z.string().nullable(),
      abono: z.number().int().nullable(),
      cargo: z.number().int().nullable(),
      /** El impuesto cobrado en la misma línea. Resta como el cargo. */
      itf: z.number().int().nullable(),
      saldo: z.number().int().nullable(),
      /**
       * Si entra en la conciliación. `false` es una línea que alguien miró y
       * decidió que nunca va a cruzar contra una boleta.
       */
      conciliable: z.boolean(),
      /**
       * Si está cruzado contra una boleta. Un movimiento conciliado no se edita
       * sin deshacer su cruce antes: cambiarle el importe por detrás dejaría
       * ese cruce firmado contra una cifra que ya no existe.
       */
      conciliado: z.boolean(),
      /** El cruce que lo bloquea, para poder ir a deshacerlo. */
      cruceId: z.string().nullable(),
    }),
  ),
});

/**
 * El checklist de estados de cuenta de un mes, agrupado por razón social.
 *
 * Es la pregunta que contabilidad hace todos los meses y que hasta ahora no
 * tenía dónde responderse: «¿ya entregué todos los extractos, o me falta
 * alguno?». Un tablero de etapas del pipeline —recibido, leído, fallado— no la
 * contesta: dice qué pasó con lo que SÍ llegó, y lo que importa es lo que no.
 *
 * Se cuenta contra las cuentas que ese mes DEBEN entregar, que es el rango
 * `entregaDesde`/`entregaHasta` del maestro. Sin ese recorte habría que pedirle
 * extracto a las 154 cuentas y las seis que de verdad faltan se perderían entre
 * ciento y pico de huecos que nadie va a llenar.
 */
export const coberturaSchema = z.object({
  /** El mes que se está mirando, `AAAA-MM`. */
  periodo: z.string(),
  /** Todos los meses que existen, para el selector. Del más nuevo al más viejo. */
  periodos: z.array(z.string()),
  /** Cuántas cuentas deben entregar este mes. */
  esperadas: z.number().int(),
  entregadas: z.number().int(),
  /** De las entregadas, cuántas cuadran contra el banco o su cadena de saldos. */
  cuadran: z.number().int(),
  conHallazgos: z.number().int(),
  /**
   * Extractos entregados de cuentas que este mes NO se esperaban.
   *
   * No es un error: es una cuenta nueva cuyo primer extracto acaba de entrar, o
   * una que se dio por cerrada y volvió. Se cuentan aparte para que
   * «32 de 38» siga siendo una cuenta honesta.
   */
  deMas: z.number().int(),
  /**
   * Si quien mira puede borrar un extracto. Hace falta rol de jefe.
   *
   * Va resuelto y no como rol por lo mismo que en la ficha: la pantalla no
   * tiene por qué conocer la escalera, y así el día que cambie no hay dos
   * sitios que actualizar. Sirve para no ofrecer en el menú de la fila una
   * acción que el servidor va a rechazar con un 403.
   */
  puedeEliminar: z.boolean(),
  empresas: z.array(
    z.object({
      empresaId: z.string(),
      razonSocial: z.string(),
      ruc: z.string(),
      /** Cuántas de sus cuentas faltan, para poder plegar las que están al día. */
      faltan: z.number().int(),
      cuentas: z.array(
        z.object({
          cuentaId: z.string(),
          banco: z.string(),
          numero: z.string(),
          moneda: z.enum(["PEN", "USD"]),
          /** `null` = esta cuenta no entregó su extracto de este mes. */
          archivoId: z.string().nullable(),
          nombre: z.string().nullable(),
          movimientos: z.number().int(),
          entro: z.number().int(),
          salio: z.number().int(),
          /**
           * Cuántas de sus líneas están fuera de la conciliación.
           *
           * Va en el resumen del mes y no solo en la ficha porque el número que
           * de verdad se mira es «cuánto queda por decidir», y ese sale de
           * restar. Sin él, un extracto con 80 líneas y 60 apartadas se lee
           * como si tuviera 80 pendientes.
           */
          noConciliables: z.number().int(),
          /** El saldo de la última línea: lo que se compara contra el PDF. */
          saldoFinal: z.number().int().nullable(),
          desde: z.string().nullable(),
          hasta: z.string().nullable(),
          /** `null` mientras no haya extracto que validar. */
          cuadra: z.boolean().nullable(),
          hallazgos: z.array(z.string()),
          /** Se esperaba este mes, o llegó de más. */
          esperada: z.boolean(),
          /**
           * Desde y hasta qué mes se le pide extracto, `AAAA-MM`.
           *
           * Viajan para que el menú de la fila pueda dejar de pedirle extracto
           * a esta cuenta SIN adivinar. Cortar la expectativa es escribir
           * `entregaHasta` en el mes anterior al que se mira, y eso solo vale
           * si ese mes no queda por detrás de `entregaDesde` —si queda, lo
           * honesto es vaciar el rango entero: nunca llegó a entregar nada.
           */
          entregaDesde: z.string().nullable(),
          entregaHasta: z.string().nullable(),
        }),
      ),
    }),
  ),
});

/**
 * El maestro de empresas y cuentas, tal como se consulta.
 *
 * Es la respuesta a «¿de quién es esta cuenta?», que es lo que decide a qué
 * razón social se imputa cada abono. Va entero en una sola llamada porque son
 * 56 empresas y 154 cuentas: paginar un maestro de ese tamaño solo añade
 * clics. Si algún día crece a miles, esto se pagina y la pantalla no cambia.
 */
export const maestrosSchema = z.object({
  /** Los contadores de la cabecera. Son del maestro entero, no del filtro. */
  empresas: z.number().int(),
  cuentas: z.number().int(),
  cuentasConExtracto: z.number().int(),
  cuentasSinCci: z.number().int(),
  empresasSinCuenta: z.number().int(),
  empresasConMovimientos: z.number().int(),

  /** Cuántas empresas pasan el filtro, para decir «12 de 56». */
  mostradas: z.number().int(),

  filas: z.array(
    z.object({
      id: z.string(),
      ruc: z.string(),
      razonSocial: z.string(),
      activa: z.boolean(),
      /** Movimientos de todas sus cuentas, para ordenar por lo que pesa. */
      movimientos: z.number().int(),
      cuentas: z.array(
        z.object({
          id: z.string(),
          banco: z.string(),
          numero: z.string(),
          moneda: z.enum(["PEN", "USD"]),
          cci: z.string().nullable(),
          alias: z.string().nullable(),
          movimientos: z.number().int(),
          extractos: z.number().int(),
          /**
           * DESDE Y HASTA QUÉ MES ENTREGÓ, SEGÚN LO SUBIDO (2026-09-09).
           *
           * Ya no se escriben a mano. Eran el único campo editable del maestro
           * y eso permitía que dijeran una cosa mientras los archivos decían
           * otra. Ahora salen de los extractos confirmados de esa cuenta: un
           * hecho comprobable, no la memoria de alguien.
           */
          entregaDesde: z.string().nullable(),
          entregaHasta: z.string().nullable(),
          /** Los meses de los que hay extracto suyo, en orden. */
          /** Dada de baja: sigue en la base por su historial, fuera de las listas. */
          activa: z.boolean(),
          meses: z.array(z.string()),
          /**
           * Los meses que faltan ENTRE el primero y el último que entregó.
           *
           * Un hueco no es «todavía no empezó» ni «ya no entrega»: es un mes
           * salteado teniendo extractos a los dos lados, y ese es el que nadie
           * echa de menos hasta que un saldo no cuadra meses después.
           */
          huecos: z.array(z.string()),
          /**
           * En qué situación está la cuenta. No es un dato del maestro sino de
           * la cobertura: una cuenta sin extracto no es una cuenta mal
           * cargada, es un mes que nadie ha entregado todavía.
           */
          estado: z.enum(["con_movimientos", "extracto_vacio", "sin_extracto"]),
          /**
           * Esta cuenta es la que hizo que su empresa entrara en el filtro.
           * Buscar «**** 2100» y que salga la empresa entera sin decir cuál de
           * sus ocho cuentas terminaba así deja el trabajo a medias.
           */
          coincide: z.boolean(),
        }),
      ),
    }),
  ),

  /** El reparto por banco: cuántas cuentas hay y de cuántas hay extracto. */
  porBanco: z.array(
    z.object({
      banco: z.string(),
      cuentas: z.number().int(),
      conExtracto: z.number().int(),
    }),
  ),
});

/**
 * Un booleano que viaja en la query.
 *
 * NO se puede usar `z.coerce.boolean()`: una query es texto, y `Boolean("false")`
 * es `true`. Con ese esquema, un conmutador apagado llegaba encendido — la
 * pantalla pedía `todasLasCuentas=false` y el servidor buscaba en las cuatro
 * cuentas, ignorando justo el dato que hace útil la búsqueda.
 */
const siONo = (porDefecto: boolean) =>
  z
    .union([z.boolean(), z.string()])
    .default(porDefecto)
    .transform((v) => (typeof v === "string" ? v === "true" || v === "1" : v));

// ===========================================================================
// CONCILIAR EL EXPEDIENTE · atar cada voucher con el movimiento que lo pagó
//
// Es la otra dirección del mismo trabajo. La revisión guiada parte del ABONO
// —«¿este abono es el pago de esa boleta?»— y la mesa parte de la lista. Esta
// parte del EXPEDIENTE: un PDF trae una o dos boletas y uno o más vouchers, y
// hasta que cada voucher no tenga su movimiento el expediente no está cerrado.
//
// La diferencia no es de gusto. Partiendo del abono, un expediente con tres
// pagos aparece tres veces sin decirlo, y nadie sabe si le falta alguno.
// Partiendo del expediente, «faltan dos de tres» se ve de una vez.
// ===========================================================================

/** Un voucher del expediente, con lo leído y con lo que ya se decidió de él. */
export const voucherAAtarSchema = z.object({
  recorte: z.string(),
  /**
   * Qué pago de esa imagen es. Una foto puede traer dos vouchers —la boleta
   * B001-122 tiene dos depósitos de S/ 500 en la misma— y cada uno se ata a su
   * propio movimiento. La imagen sola ya no identifica un pago.
   */
  pagoN: z.number().int(),
  /** Cuántos pagos vio la lectura en la imagen. `null` si no lo dijo. */
  dePagos: z.number().int().nullable(),
  pagina: z.number().int().nullable(),
  /** El sitio dentro de la página, de arriba abajo. */
  orden: z.number().int().nullable(),

  importe: z.number().int().nullable(),
  moneda: z.enum(["PEN", "USD"]).nullable(),
  fechaPago: z.string().nullable(),
  hora: z.string().nullable(),
  operacion: z.string().nullable(),

  /** Yape, Plin, transferencia, depósito… `null` si no se pudo decir. */
  medio: z.string().nullable(),
  /** Cómo llegó la imagen: captura de pantalla o foto de papel. */
  origenImagen: z.string().nullable(),
  clase: z.string().nullable(),
  legible: z.boolean(),

  banco: z.string().nullable(),
  cuentaDestino: z.string().nullable(),
  pagador: z.string().nullable(),
  beneficiario: z.string().nullable(),

  /**
   * Si el dinero cruzó de un banco a otro. Abre la ventana dos días más: una
   * interbancaria no se abona el mismo día.
   */
  interbancaria: z.boolean(),

  /**
   * LA CUENTA DEL GRUPO A LA QUE APUNTA, ya resuelta contra el maestro.
   *
   * Es la pieza que hace útil la pantalla: con los últimos dígitos del voucher
   * se sabe en QUÉ extracto buscar, y buscar en una cuenta en vez de en los
   * cuatro bancos es la diferencia entre cinco candidatos y ciento veinte.
   */
  cuentaDelGrupo: z
    .object({
      id: z.string(),
      banco: z.string(),
      numero: z.string(),
      moneda: z.string(),
      razonSocial: z.string().nullable(),
    })
    .nullable(),

  /**
   * Si una persona ya corrigió lo que leyó la máquina: quién y cuándo.
   *
   * Es lo que hace auditable una corrección. Un dato que alguien cambió y un
   * dato que salió del lector se ven igual en la pantalla si no se dice, y
   * seis meses después nadie puede responder por qué el importe del voucher no
   * es el que imprime el papel.
   */
  correccion: z
    .object({
      veredicto: z.enum(["correcto", "corregido", "no_es_voucher", "ilegible"]),
      quien: z.string().nullable(),
      momento: z.string(),
      nota: z.string().nullable(),
    })
    .nullable(),

  /** Si ya está atado: a qué movimiento, y quién lo ató. */
  enlace: z
    .object({
      movimientoId: z.string(),
      fecha: z.string(),
      banco: z.string(),
      importe: z.number().int(),
      moneda: z.enum(["PEN", "USD"]),
      operacion: z.string().nullable(),
      quien: z.string().nullable(),
      momento: z.string(),
      automatico: z.boolean(),
    })
    .nullable(),

  /**
   * Si alguien decidió que este pago NO está en ningún extracto cargado.
   *
   * Se guarda la decisión, no la ausencia: sin esto, «ya lo miré y no está» y
   * «todavía no lo he mirado» se ven exactamente igual.
   */
  sinAbono: z
    .object({
      motivo: z.string().nullable(),
      quien: z.string().nullable(),
      momento: z.string(),
    })
    .nullable(),
});

export const expedienteAConciliarSchema = z.object({
  /** Dónde está este expediente dentro de la cola, para poder recorrerla. */
  cola: z.object({
    indice: z.number().int(),
    total: z.number().int(),
    anteriorId: z.string().nullable(),
    siguienteId: z.string().nullable(),
  }),

  archivo: z.object({
    id: z.string(),
    nombre: z.string(),
    paginas: z.number().int().nullable(),
    subidoEl: z.string(),
  }),

  /** La ficha del cliente: es lo que dice de QUIÉN es este pago. */
  cliente: z
    .object({
      nombre: z.string().nullable(),
      documento: z.string().nullable(),
      /** Separados ya en lista: 62 de 218 fichas traen dos. */
      telefonos: z.array(z.string()),
      distrito: z.string().nullable(),
      ocupacion: z.string().nullable(),
      pagina: z.number().int().nullable(),
    })
    .nullable(),

  /** Lo que se emitió: una boleta, o dos, o una y su nota de crédito. */
  boletas: z.array(
    z.object({
      id: z.string(),
      serieNumero: z.string().nullable(),
      tipo: z.string(),
      fechaEmision: z.string().nullable(),
      moneda: z.enum(["PEN", "USD"]),
      total: z.number().int().nullable(),
      pagina: z.number().int().nullable(),
      /**
       * El recorte de la imagen de la boleta, cuando el PDF la trae como foto.
       *
       * Sirve para lo mismo que el del voucher: reconocer el papel de un
       * vistazo sin abrir el PDF entero. `null` en las boletas que salieron
       * del texto y no tienen imagen propia.
       */
      recorte: z.string().nullable(),
      cliente: z.string().nullable(),
      anulado: z.boolean(),
      /** Cuánto de esta boleta ya está cobrado, sumando sus cruces. */
      cobrado: z.number().int(),
      estadoEtiqueta: z.string(),
      estadoTono: z.enum(["ok", "warn", "bad", "neutro"]),
    }),
  ),

  /**
   * El cuadre del expediente: lo que se emitió, lo que se pagó y lo atado.
   *
   * `mezclaMonedas` porque un expediente con una boleta en dólares y un
   * voucher en soles no se puede sumar, y sumarlo igual daría un cuadre falso.
   */
  cuadre: z.object({
    moneda: z.enum(["PEN", "USD"]),
    boletas: z.number().int(),
    vouchers: z.number().int(),
    atado: z.number().int(),
    mezclaMonedas: z.boolean(),
  }),

  vouchers: z.array(voucherAAtarSchema),
});

/** Los movimientos que podrían ser el pago de un voucher. */
export const candidatosSchema = z.object({
  /** Con qué criterio se buscó, para poder enseñarlo y moverlo. */
  criterio: z.object({
    fecha: z.string().nullable(),
    desde: z.string().nullable(),
    hasta: z.string().nullable(),
    importe: z.number().int().nullable(),
    moneda: z.enum(["PEN", "USD"]),
    diasAntes: z.number().int(),
    diasDespues: z.number().int(),
    /** En céntimos, como todo el dinero de este sistema. */
    tolerancia: z.number().int(),
    cuentaId: z.string().nullable(),
    cuenta: z.string().nullable(),
    todasLasCuentas: z.boolean(),
    ocultarConciliados: z.boolean(),
    /** Sobre cuántos movimientos se buscó, para saber cuánto recorta. */
    universo: z.number().int(),
  }),

  filas: z.array(
    z.object({
      id: z.string(),
      fecha: z.string(),
      /** Días entre el voucher y el abono. Negativo = el abono es anterior. */
      dias: z.number().int().nullable(),
      banco: z.string(),
      cuenta: z.string().nullable(),
      moneda: z.enum(["PEN", "USD"]),
      importe: z.number().int(),
      /** Abono menos voucher, en céntimos. Cero es coincidencia exacta. */
      diferencia: z.number().int().nullable(),
      descripcion: z.string(),
      detalle: z.string().nullable(),
      operacion: z.string().nullable(),
      /**
       * Cómo se parece el número de operación del banco al del voucher.
       *
       * No filtra: cada banco numera a su manera. Se enseña porque el caso
       * corriente —el mismo número con un dígito distinto— lo reconoce una
       * persona de un vistazo y ninguna regla se atreve a darlo por bueno.
       */
      operacionCompara: z.enum([
        "identico",
        "mismo_final",
        "difieren",
        "no_comparable",
      ]),
      estado: z.string(),
      estadoEtiqueta: z.string(),
      estadoTono: z.enum(["ok", "warn", "bad", "loan", "neutro"]),
      /** Si ya está cruzado con una boleta: con cuál. */
      tomadoPor: z.string().nullable(),
      /** Lo que le queda por aplicar, en céntimos. */
      restante: z.number().int(),
      /**
       * El nombre de quien pagó aparece escrito en la glosa del banco.
       *
       * `de` dice de dónde salió el nombre que casó. No es un detalle: el
       * pagador del voucher y el cliente de la boleta son personas distintas a
       * menudo —paga un familiar—, así que «coincide el nombre» significa una
       * cosa u otra según cuál de los dos casó.
       */
      nombreEnLaGlosa: z
        .object({
          casados: z.array(z.string()),
          fuerte: z.boolean(),
          de: z.enum(["voucher", "boleta"]),
        })
        .nullable(),
      /**
       * El teléfono de la ficha, escrito en la glosa del banco.
       *
       * Es la única evidencia que queda en un Yape o un Plin: no traen número
       * de operación ni cuenta de destino, y quien envía no suele ser el
       * cliente de la boleta —es un familiar—, así que el nombre tampoco
       * desempata. El móvil sí, y la ficha lo trae.
       */
      telefonoEnLaGlosa: z.string().nullable(),
      /** Dato a dato, qué cuadra y qué no. Es lo que se mira antes de atar. */
      senales: z.array(
        z.object({
          clave: z.string(),
          texto: z.string(),
          veredicto: z.enum(["coincide", "difiere", "no_se_puede"]),
        }),
      ),
      /** Cuántas señales cuadran, para ordenar. No es una probabilidad. */
      aciertos: z.number().int(),
    }),
  ),

  total: z.number().int(),
});

export const contract = c.router(
  {
    /** Quién soy. El frontend lo usa para pintar el usuario y para el guard. */
    yo: {
      method: "GET",
      path: "/yo",
      responses: {
        200: usuarioSchema,
        401: errorBody,
      },
      summary: "Usuario de la sesión actual",
    },

    /**
     * Quién tiene acceso. Solo para `administrador`.
     *
     * Los cinco roles son los MISMOS del ERP, copiados tal cual para que el día
     * que este módulo se mude allá el mapeo sea directo. No conviene inventar
     * roles nuevos aquí.
     */
    usuarios: {
      method: "GET",
      path: "/usuarios",
      responses: {
        200: z.object({
          usuarios: z.array(
            z.object({
              id: z.string(),
              email: z.string(),
              nombre: z.string(),
              rol: z.enum([
                "administrador",
                "gerente",
                "jefe",
                "operativo",
                "visor",
              ]),
              activo: z.boolean(),
              creado: z.string(),
              /** Cuántas sesiones abiertas tiene ahora mismo. */
              sesiones: z.number().int(),
              /** Si es uno mismo: la pantalla no deja cortarse el paso solo. */
              soyYo: z.boolean(),
            }),
          ),
        }),
        401: errorBody,
        403: errorBody,
      },
      summary: "Quién tiene acceso al módulo",
    },

    crearUsuario: {
      method: "POST",
      path: "/usuarios",
      body: z.object({
        email: z.string().email("Eso no parece un correo"),
        nombre: z.string().min(2).max(120),
        rol: z.enum(["administrador", "gerente", "jefe", "operativo", "visor"]),
        /** Si no se da, se genera una y se dice UNA vez. */
        clave: z.string().min(8).max(200).optional(),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          /** Solo cuando la generó el sistema: no se guarda en ningún lado. */
          clave: z.string().nullable(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Dar de alta a alguien",
    },

    cambiarRol: {
      method: "POST",
      path: "/usuarios/:id/rol",
      body: z.object({
        rol: z.enum(["administrador", "gerente", "jefe", "operativo", "visor"]),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Cambiarle el rol a alguien",
    },

    cambiarAcceso: {
      method: "POST",
      path: "/usuarios/:id/acceso",
      body: z.object({ activo: z.boolean() }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Dar o cortar el paso, con efecto inmediato",
    },

    restablecerClave: {
      method: "POST",
      path: "/usuarios/:id/clave",
      body: z.object({ clave: z.string().min(8).max(200).optional() }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          clave: z.string(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        422: errorBody,
      },
      summary: "Poner una contraseña nueva y decirla una sola vez",
    },

    contexto: {
      method: "GET",
      path: "/contexto",
      responses: { 200: contextoSchema, 401: errorBody },
      summary: "El periodo en el que se está trabajando",
    },

    inicio: {
      method: "GET",
      path: "/inicio",
      query: z.object({
        /**
         * Por qué recortar la portada. Todos opcionales y acumulables.
         *
         * Contabilidad lo pidió por «grupo de razón social, cuentas, moneda».
         * El de razón social hoy ofrece una sola opción —los movimientos son
         * de una empresa de las 56— pero se construye igual: es el mes que
         * viene. Donde está el valor ahora es en cuenta y moneda.
         */
        empresaId: z.string().optional(),
        cuentaId: z.string().optional(),
        moneda: z.enum(["PEN", "USD"]).optional(),
      }),
      responses: { 200: inicioSchema, 401: errorBody },
      summary: "Lo que hay que decidir hoy",
    },

    revision: {
      method: "GET",
      path: "/revision",
      query: z.object({
        indice: z.coerce.number().int().min(0).default(0),
        /**
         * En qué orden sale la cola.
         *
         * `importe` —lo de siempre— pone delante lo que más dinero mueve: si
         * alguien solo puede revisar veinte casos hoy, que sean los veinte que
         * más pesan en el cierre.
         *
         * `duda` pone delante aquello de lo que el motor está menos seguro,
         * que es lo que pedía el issue del score: lo dudoso primero. Se mide
         * por el puntaje del MEJOR candidato de cada abono — un abono cuyo
         * mejor candidato saca 0,15 necesita una persona mucho más que uno con
         * 0,85, aunque mueva menos dinero.
         *
         * Son dos formas legítimas de repartir un rato de trabajo, y por eso
         * es un conmutador y no una decisión tomada por quien programó.
         */
        orden: z.enum(["importe", "duda"]).default("importe"),
        /**
         * En qué montón trabajar, para poder ir de menor a mayor dificultad.
         *
         * Los cortes NO son los que pidió el área —«mayores a 80»— porque
         * ningún candidato de la base pasa de 0,65: lo fácil ya lo cierra el
         * motor y no llega a la cola. Viven con nombre en
         * `motor/cruce/dificultad.ts`.
         *
         * `sin_candidato` es el cuarto montón, que el esquema de tres no
         * nombraba y es el grueso del trabajo: 166 de 231 en la base cargada.
         */
        banda: z
          .enum(["facil", "normal", "dificil", "sin_candidato"])
          .optional(),
      }),
      responses: { 200: revisionSchema, 401: errorBody },
      summary: "El siguiente movimiento por revisar, de uno en uno",
    },

    /**
     * LA COLA POR BOLETA, que es la que de verdad se acaba.
     *
     * La revisión guiada se armaba desde los ABONOS que tienen candidato, y con
     * eso se quedaba vacía teniendo catorce expedientes esperando: el motor no
     * propone nada cuando el voucher no explica el total de la boleta —un pago
     * a plazos, por ejemplo— y esas boletas no aparecían en ningún sitio.
     *
     * Contabilidad lo dijo así: «se supone que ahí tienen que aparecer todos
     * los documentos de los clientes que tienen que estar vinculados a un
     * movimiento bancario; esa es la cola». Y es la buena: son catorce, no
     * ciento doce, y son las que impiden cerrar el mes.
     *
     * La cola por abono se queda, porque un abono sin documento también es
     * trabajo — solo que de otra clase: hay que pedirle el expediente a
     * finanzas, no buscarle el pago.
     */
    revisionBoletas: {
      method: "GET",
      path: "/revision/boletas",
      query: z.object({
        indice: z.coerce.number().int().min(0).default(0),
      }),
      responses: {
        200: z.object({
          indice: z.number().int(),
          /** Cuántas boletas quedan por vincular a un abono. */
          total: z.number().int(),
          caso: z
            .object({
              comprobanteId: z.string(),
              serieNumero: z.string().nullable(),
              cliente: z.string().nullable(),
              importe: z.number().int().nullable(),
              moneda: z.enum(["PEN", "USD"]),
              fecha: z.string().nullable(),
              estado: z.string(),
              archivoId: z.string(),
              /** Cuánto lleva cobrado ya, si es un pago a plazos. */
              yaAplicado: z.number().int(),
              /** Lo que dicen sus vouchers, tal como se leyeron. */
              vouchers: z.array(
                z.object({
                  recorte: z.string(),
                  importe: z.number().int().nullable(),
                  moneda: z.string().nullable(),
                  fechaPago: z.string().nullable(),
                  operacion: z.string().nullable(),
                  banco: z.string().nullable(),
                  medio: z.string().nullable(),
                  leido: z.boolean(),
                }),
              ),
              /**
               * Por qué el motor no propuso nada, cuando no propuso.
               *
               * `null` si sí propuso. Es el dato que convierte una pantalla
               * vacía en una tarea: «el voucher dice 700 y la boleta 1.000»
               * se resuelve buscando el abono a mano, y «sus vouchers están
               * sin leer» se resuelve pagando al modelo.
               */
              porQueNoPropone: z.string().nullable(),
              /** Lo que el motor sí propone, si propone algo. */
              candidatos: z.array(
                z.object({
                  movimientoId: z.string(),
                  fecha: z.string(),
                  importe: z.number().int(),
                  moneda: z.enum(["PEN", "USD"]),
                  banco: z.string(),
                  descripcion: z.string().nullable(),
                  operacion: z.string().nullable(),
                  motivo: z.string().nullable(),
                  confianza: z.string().nullable(),
                  puntaje: z.string(),
                }),
              ),
            })
            .nullable(),
        }),
        401: errorBody,
      },
      summary: "Las boletas que faltan por vincular a un abono del banco",
    },

    decidir: {
      method: "POST",
      path: "/decision",
      body: decisionSchema,
      responses: { 200: decisionHechaSchema, 401: errorBody, 422: errorBody },
      summary: "Confirmar, descartar o dejar en duda un movimiento",
    },

    /**
     * La tanda que el motor da por cierta, PARA MIRARLA antes de firmar.
     *
     * Desde que nada se concilia solo, un mes trae doscientas propuestas
     * obvias. Firmarlas de una en una es media mañana, y media mañana de
     * pulsar «sí» doscientas veces acaba en pulsar sin leer — que es peor que
     * no tener la compuerta, porque parece una revisión y no lo es.
     *
     * Por eso son dos pasos y no uno: esto DEVUELVE la lista, con el abono y
     * la boleta de cada línea, para poder recorrerla; y `confirmarTanda`
     * escribe solo los pares que se le nombran.
     */
    tandaDelMotor: {
      method: "GET",
      path: "/revision/tanda",
      query: z.object({
        /**
         * Hasta qué etiqueta del motor entra en la tanda.
         *
         * `exacto` es la de por defecto y la única que se ofrece de entrada: es
         * lo que el motor antes aplicaba sin preguntar. Las otras existen para
         * quien quiera mirar más abajo a sabiendas.
         */
        confianza: z.enum(["exacto", "alta", "media"]).default("exacto"),
        limite: z.coerce.number().int().min(1).max(500).default(200),
      }),
      responses: {
        200: z.object({
          /** Cuántas cumplen en todo el periodo. */
          total: z.number().int(),
          filas: z.array(
            z.object({
              movimientoId: z.string(),
              comprobanteId: z.string(),
              fecha: z.string(),
              importe: z.number().int(),
              moneda: z.enum(["PEN", "USD"]),
              descripcion: z.string().nullable(),
              serieNumero: z.string().nullable(),
              cliente: z.string().nullable(),
              totalBoleta: z.number().int().nullable(),
              confianza: z.string(),
              puntaje: z.string(),
              motivo: z.string().nullable(),
            }),
          ),
        }),
        401: errorBody,
      },
      summary: "Las propuestas que el motor da por ciertas, para revisarlas",
    },

    /**
     * Firmar una tanda de propuestas de una vez.
     *
     * Sigue habiendo una persona decidiendo, y queda escrito quién y cuántas.
     * Se nombran los PARES uno a uno y no un criterio: quien firma responde por
     * la lista que vio, no por una consulta que puede haber cambiado de
     * resultado entre que se pintó la pantalla y se pulsó el botón.
     */
    confirmarTanda: {
      method: "POST",
      path: "/revision/tanda",
      body: z.object({
        pares: z
          .array(
            z.object({
              movimientoId: z.string(),
              comprobanteId: z.string(),
            }),
          )
          .min(1)
          .max(500),
        confirmo: z.literal(true),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          firmados: z.number().int(),
          /** Los que ya no estaban donde se los dejó: se dicen, no se ocultan. */
          saltados: z.array(
            z.object({ movimientoId: z.string(), porque: z.string() }),
          ),
        }),
        401: errorBody,
        403: errorBody,
        422: errorBody,
      },
      summary: "Confirmar de una vez las propuestas que se acaban de revisar",
    },

    tipoCambio: {
      method: "GET",
      path: "/tipo-cambio",
      responses: {
        200: z.object({
          periodo: z.string(),
          dias: z.array(
            z.object({
              fecha: z.string(),
              compra: z.string().nullable(),
              venta: z.string().nullable(),
              fuente: z.string().nullable(),
              /** Cuántos movimientos en dólares hay ese día. */
              movimientosUsd: z.number().int(),
            }),
          ),
          /** Los días que tienen dólares y todavía no tienen cambio. */
          faltan: z.number().int(),
        }),
        401: errorBody,
      },
      summary: "El tipo de cambio del periodo, día por día",
    },

    guardarTipoCambio: {
      method: "POST",
      path: "/tipo-cambio",
      body: z.object({
        fecha: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, "La fecha va como 2026-01-31"),
        compra: z.coerce.number().positive(),
        venta: z.coerce.number().positive(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Poner el tipo de cambio de un día a mano",
    },

    bitacora: {
      method: "GET",
      path: "/bitacora",
      query: z.object({
        usuario: z.string().optional(),
        accion: z.string().optional(),
        entidad: z.string().optional(),
        /** Para abrir el rastro de UNA cosa: este movimiento, este archivo. */
        entidadId: z.string().optional(),
        desde: z.string().optional(),
        hasta: z.string().optional(),
        pagina: z.coerce.number().int().min(0).default(0),
        porPagina: z.coerce.number().int().min(1).max(200).default(50),
      }),
      responses: {
        200: z.object({
          total: z.number().int(),
          asientos: z.array(
            z.object({
              id: z.string(),
              momento: z.string(),
              usuario: z.string().nullable(),
              accion: z.string(),
              /** La misma acción, dicha en castellano. */
              queHizo: z.string(),
              entidad: z.string().nullable(),
              entidadId: z.string().nullable(),
              nota: z.string().nullable(),
              /**
               * Qué cambió, campo por campo y ya calculado.
               *
               * El JSON crudo no se manda: la pantalla no tiene que volver a
               * decidir qué es un cambio, y quien lea la respuesta de la API
               * ve lo mismo que quien mira la pantalla.
               */
              cambios: z.array(
                z.object({
                  campo: z.string(),
                  antes: z.string().nullable(),
                  despues: z.string().nullable(),
                }),
              ),
            }),
          ),
          /** Para llenar los filtros con lo que existe, no con una lista fija. */
          usuarios: z.array(z.string()),
          acciones: z.array(
            z.object({ accion: z.string(), queHizo: z.string() }),
          ),
        }),
        401: errorBody,
      },
      summary: "Quién cambió qué y cuándo",
    },

    borrarTipoCambio: {
      method: "POST",
      path: "/tipo-cambio/borrar",
      body: z.object({ fecha: z.string() }),
      responses: { 200: z.object({ ok: z.literal(true) }), 401: errorBody },
      summary: "Quitar el tipo de cambio de un día",
    },

    marcarPrestamo: {
      method: "POST",
      path: "/prestamo",
      body: z.object({
        movimientoIds: z.array(z.string()).min(1).max(500),
        /** `false` para deshacerlo: vuelve a la cola de decisión. */
        esPrestamo: z.boolean().default(true),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          marcados: z.number().int(),
          dice: z.string(),
        }),
        401: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Marcar abonos como movimiento entre empresas del grupo",
    },

    anotar: {
      method: "POST",
      path: "/nota",
      body: z.object({
        movimientoId: z.string(),
        nota: z.string().min(1).max(500),
      }),
      responses: {
        200: z.object({ ok: z.literal(true) }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Dejar una nota en el expediente de un movimiento",
    },

    /**
     * Dar de alta un mes.
     *
     * Hacía falta y no estaba: el mes solo nacía desde la terminal, así que al
     * soltar un extracto de un mes que nadie había creado —febrero, sin ir más
     * lejos— no había forma de decir a cuál pertenecía. Quien carga los
     * papeles no tiene por qué pedirle a sistemas que le abra el mes.
     */
    /**
     * MOVER EXPEDIENTES DE MES, EN LOTE (2026-09-09).
     *
     * El caso: alguien sube el montón del mes y se equivoca de mes. Hasta
     * ahora no había vuelta atrás desde la web —el periodo se fijaba al
     * recibir el archivo— y corregirlo pedía borrar y volver a subir 273 PDFs.
     *
     * Mueve el archivo Y lo que cuelga de él: sus comprobantes y sus vouchers,
     * que llevan su propio periodo. Dejar el archivo en marzo con las boletas
     * en febrero es peor que el error original, porque cada pantalla mira una
     * de las dos tablas.
     *
     * LO YA CONCILIADO NO SE MUEVE. Un cruce ata la boleta a un abono que vive
     * en el mes de su extracto; llevarse la boleta a otro mes dejaría el cruce
     * a caballo entre dos periodos y ningún cierre volvería a cuadrar. Se
     * rechazan esos y se dice cuáles, en vez de mover a medias.
     */
    moverExpedientes: {
      method: "POST",
      path: "/expedientes/mover",
      body: z.object({
        archivoIds: z.array(z.string().min(1)).min(1).max(500),
        periodoId: z.string().min(1),
      }),
      responses: {
        200: z.object({
          movidos: z.number().int(),
          /** Los que no se movieron, con el motivo. */
          rechazados: z.array(
            z.object({
              archivoId: z.string(),
              nombre: z.string(),
              motivo: z.string(),
            }),
          ),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        422: errorBody,
      },
      summary: "Llevar expedientes a otro mes, de a uno o en lote",
    },

    /**
     * Borrar expedientes cargados, en lote.
     *
     * Con la misma regla: lo conciliado no se toca. Borrar una boleta cruzada
     * dejaría el cruce apuntando a algo que ya no existe.
     */
    eliminarExpedientes: {
      method: "POST",
      path: "/expedientes/eliminar",
      body: z.object({
        archivoIds: z.array(z.string().min(1)).min(1).max(500),
        confirmo: z.literal(true),
      }),
      responses: {
        200: z.object({
          borrados: z.number().int(),
          rechazados: z.array(
            z.object({
              archivoId: z.string(),
              nombre: z.string(),
              motivo: z.string(),
            }),
          ),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        422: errorBody,
      },
      summary: "Borrar expedientes cargados, de a uno o en lote",
    },

    crearPeriodo: {
      method: "POST",
      path: "/periodo",
      body: z.object({
        /** `AAAA-MM`. El día no se pide: un periodo es un mes entero. */
        nombre: z
          .string()
          .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "El mes tiene que ser AAAA-MM"),
      }),
      responses: {
        200: z.object({
          id: z.string(),
          nombre: z.string(),
          /** `true` si ya existía: pedirlo dos veces no es un error. */
          yaEstaba: z.boolean(),
        }),
        401: errorBody,
        403: errorBody,
        422: errorBody,
      },
      summary: "Abrir un mes nuevo para poder cargarle documentos",
    },

    cerrarPeriodo: {
      method: "POST",
      path: "/periodo/cerrar",
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          periodo: z.string(),
          dice: z.string(),
        }),
        401: errorBody,
        409: errorBody,
      },
      summary: "Cerrar el periodo, si se cumplen los requisitos",
    },

    reabrirPeriodo: {
      method: "POST",
      path: "/periodo/reabrir",
      body: z.object({ motivo: z.string().min(3).max(300) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          periodo: z.string(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
      },
      summary: "Volver a abrir un periodo cerrado, dejando dicho por qué",
    },

    buscarBoletas: {
      method: "GET",
      path: "/boletas",
      query: z.object({
        texto: z.string().optional(),
        /** Para poner primero las que cuadran con este importe. */
        importe: z.coerce.number().int().optional(),
        moneda: z.enum(["PEN", "USD"]).optional(),
      }),
      responses: {
        200: z.object({
          boletas: z.array(
            z.object({
              id: z.string(),
              serieNumero: z.string().nullable(),
              cliente: z.string().nullable(),
              fechaEmision: z.string().nullable(),
              total: z.number().int().nullable(),
              moneda: z.enum(["PEN", "USD"]),
              estado: z.string(),
              /** Si algún voucher suyo tiene ese importe. */
              cuadra: z.boolean(),
            }),
          ),
        }),
        401: errorBody,
      },
      summary: "Buscar una boleta para enlazarla a mano",
    },

    decidirVarias: {
      method: "POST",
      path: "/decision/varias",
      body: z.object({
        movimientoIds: z.array(z.string()).min(1).max(500),
        respuesta: z.enum(["si", "no", "no_se"]),
      }),
      responses: {
        200: z.object({
          hechas: z.number().int(),
          saltadas: z.number().int(),
          dice: z.string(),
          /** Por qué se saltó cada una, agrupado. */
          motivos: z.array(
            z.object({ motivo: z.string(), cuantas: z.number().int() }),
          ),
        }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Decidir de golpe sobre varios movimientos",
    },

    corregir: {
      method: "POST",
      path: "/verificacion",
      body: correccionSchema,
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          /** Cuántas filas de voucher comparten esa imagen y quedaron al día. */
          vouchersActualizados: z.number().int(),
        }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Corregir a mano lo que el lector entendió de un voucher",
    },

    /** Lo que está subido y esperando que alguien lo mire. */
    enRevision: {
      method: "GET",
      path: "/carga",
      responses: {
        200: z.object({
          periodo: z.string(),
          /**
           * A QUÉ MES Y A QUÉ RAZÓN SOCIAL SE DECLARA QUE PERTENECE LO QUE SE
           * SUBE (2026-09-09).
           *
           * Se pide en un formulario antes de soltar los archivos. No es
           * catalogación: cada razón social tiene sus propias cuentas de banco,
           * y saber de quién es lo que entra es lo que después permite
           * reconocer un préstamo entre razones sociales. Además, lo declarado
           * se contrasta con lo que dice el papel.
           */
          periodos: z.array(z.object({ id: z.string(), nombre: z.string() })),
          empresas: z.array(
            z.object({
              id: z.string(),
              ruc: z.string(),
              razonSocial: z.string(),
              /** Cuántas cuentas de banco tiene: es su huella en el grupo. */
              cuentas: z.number().int(),
            }),
          ),
          /**
           * El resumen del mes con lo YA confirmado: cuántos movimientos, cuánto
           * entró y cuánto salió, por cuenta y por moneda.
           *
           * Es el paso 2 del guion de contabilidad, con sus palabras: «que el
           * programa haga un cálculo de la suma y resta de movimientos para
           * obtener el total del mes como un resumen de cantidad de movimientos,
           * dinero entrado y salido». Hasta ahora ese cálculo existía —el cuadre
           * lo hace por archivo— pero solo se enseñaba como semáforo: verde o
           * rojo, sin las cifras que hacen que alguien pueda comprobarlo contra
           * el PDF que tiene delante.
           *
           * Va por moneda y sin sumar las dos. Un total que junte soles y
           * dólares no existe mientras no haya tipo de cambio del día, y aquí
           * todavía no lo hay.
           */
          mes: z.object({
            extractos: z.number().int(),
            movimientos: z.number().int(),
            porMoneda: z.array(
              z.object({
                moneda: z.enum(["PEN", "USD"]),
                movimientos: z.number().int(),
                entro: z.number().int(),
                salio: z.number().int(),
              }),
            ),
            porCuenta: z.array(
              z.object({
                archivoId: z.string(),
                banco: z.string(),
                numero: z.string(),
                empresa: z.string(),
                moneda: z.enum(["PEN", "USD"]),
                movimientos: z.number().int(),
                entro: z.number().int(),
                salio: z.number().int(),
                /** El saldo que imprime la última línea del extracto. */
                saldoFinal: z.number().int().nullable(),
                /**
                 * QUÉ MES CUBRE ESTE EXTRACTO, sacado de sus propios
                 * movimientos.
                 *
                 * La tabla decía cuenta, movimientos, entró, salió y saldo, y
                 * no decía de cuándo. Con dos extractos de la misma cuenta —el
                 * de enero y el de febrero— las dos filas se ven idénticas, y
                 * el saldo final que hay que comparar contra el PDF depende de
                 * cuál sea. Los de Alfin además cubren varios meses en un solo
                 * archivo, así que van los dos extremos y no un mes suelto.
                 */
                desde: z.string().nullable(),
                hasta: z.string().nullable(),
              }),
            ),
          }),
          borradores: z.array(
            z.object({
              id: z.string(),
              nombre: z.string(),
              bytes: z.number().int(),
              clase: z.enum(["estado_cuenta", "comprobante"]),
              /** Qué se entendió, ya con las correcciones aplicadas encima. */
              banco: z.string().nullable(),
              /** De dónde salió el banco. `nombre` significa que lo dijo el
               *  nombre del archivo y no su contenido: eso hay que mirarlo. */
              origenBanco: z.enum(["texto", "nombre", "ninguno"]),
              moneda: z.enum(["PEN", "USD"]).nullable(),
              empresa: z.string().nullable(),
              cuenta: z.string().nullable(),
              /**
               * La cuenta del maestro a la que apunta, por id.
               *
               * Va además del número porque la pantalla tiene que poder dejar
               * ELEGIDA la cuenta en el desplegable, no solo escrita. Con el
               * número no alcanza: dos bancos distintos repiten numeración, y
               * lo que se guarda al corregir es el id.
               */
              cuentaId: z.string().nullable(),
              /** Por qué se resolvió esa cuenta, en castellano. */
              porQueLaCuenta: z.string().nullable(),
              desde: z.string().nullable(),
              hasta: z.string().nullable(),
              movimientos: z.number().int(),
              /** Los totales del banco contra los que salen de sumar. */
              cuadre: z
                .object({
                  declaradoAbonos: z.number().int().nullable(),
                  calculadoAbonos: z.number().int(),
                  declaradoCargos: z.number().int().nullable(),
                  calculadoCargos: z.number().int(),
                  cuadra: z.boolean(),
                })
                .nullable(),
              hallazgos: z.array(
                z.object({
                  codigo: z.string(),
                  gravedad: z.enum(["error", "aviso"]),
                  mensaje: z.string(),
                }),
              ),
              /**
               * Las líneas leídas del extracto, para poder corregirlas antes de
               * confirmar. Es el paso 3 del guion: «poder editarlo, tipo Excel».
               *
               * Van solo en los estados de cuenta; un expediente no tiene filas
               * que editar. Y van SIEMPRE, no bajo demanda: 158 líneas del
               * extracto más grande de enero son 30 KB de JSON, y una segunda
               * petición por archivo para pedirlas complicaría la pantalla más
               * de lo que ahorra.
               */
              filas: z.array(filaExtractoSchema),
              /** Los índices de `filas` que tocó una persona. */
              filasTocadas: z.array(z.number().int()),
              /** `true` si se puede confirmar sin que quede algo a medias. */
              listo: z.boolean(),
              /** Lo que impide confirmarlo, si algo lo impide. */
              falta: z.string().nullable(),
              error: z.string().nullable(),
              tocadoAMano: z.boolean(),
            }),
          ),
        }),
        401: errorBody,
      },
      summary: "Los archivos subidos que esperan revisión",
    },

    corregirCarga: {
      method: "POST",
      path: "/carga/:id",
      pathParams: z.object({ id: z.string() }),
      body: z.object({
        banco: z.enum(["BCP", "BBVA", "INTERBANK", "ALFIN"]).nullish(),
        moneda: z.enum(["PEN", "USD"]).nullish(),
        /** La cuenta del maestro a la que pertenece este extracto. */
        cuentaId: z.string().nullish(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Corregir lo que se detectó mal antes de confirmarlo",
    },

    /**
     * Guardar las filas corregidas de un extracto, todas de una vez.
     *
     * En lote y no una petición por celda: son las palabras del área —«poder
     * editar múltiples datos y subirlo todo en conjunto»— y además es lo que
     * permite recalcular el cuadre una sola vez sobre el conjunto. Fila a fila,
     * el extracto pasaría por estados intermedios que no cuadran y el semáforo
     * parpadearía en rojo mientras se escribe.
     *
     * Solo sobre el BORRADOR, antes de confirmar. Un movimiento que ya entró a
     * la base puede estar conciliado, y editarle el importe por detrás dejaría
     * un cruce firmado contra una cifra que ya no existe.
     */
    guardarFilas: {
      method: "POST",
      path: "/carga/:id/filas",
      pathParams: z.object({ id: z.string() }),
      body: z.object({
        filas: z.array(filaExtractoSchema).max(2000),
        /** Los índices que tocó la persona, para poder marcarlos después. */
        tocadas: z.array(z.number().int()).max(2000),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          /** Recalculado sobre lo que se acaba de guardar. */
          cuadre: cuadreSchema.nullable(),
          hallazgos: z.array(hallazgoSchema),
          /** Si con estos cambios se puede confirmar. */
          listo: z.boolean(),
        }),
        401: errorBody,
        404: errorBody,
        422: errorBody,
      },
      summary: "Corregir las líneas del extracto antes de meterlo a la base",
    },

    /**
     * La revisión de completitud de un lote: qué expedientes están enteros.
     *
     * Es lo que pidió el área para el ZIP: «primero se hace la revisión de la
     * completitud de la información, si tiene todas las secciones, y te sale un
     * cuadro de cuáles cumplieron con los requisitos y cuáles no».
     *
     * Hoy cargar el mes es soltar 273 PDFs y esperar. Si veinte vienen
     * incompletos se descubre de uno en uno y semanas después, cuando su abono
     * no cierra y nadie sabe que le faltaba el voucher.
     */
    lote: {
      method: "GET",
      path: "/carga/lote/:loteId",
      pathParams: z.object({ loteId: z.string() }),
      responses: {
        200: z.object({
          loteId: z.string(),
          subidoAt: z.string().nullable(),
          /** Cuántos archivos, y cuántos siguen en el pipeline. */
          archivos: z.number().int(),
          leidos: z.number().int(),
          enCurso: z.number().int(),
          fallados: z.number().int(),
          /** El veredicto de completitud, contado. */
          completos: z.number().int(),
          incompletos: z.number().int(),
          expedientes: z.array(
            z.object({
              archivoId: z.string(),
              nombre: z.string(),
              estado: z.string(),
              serieNumero: z.string().nullable(),
              cliente: z.string().nullable(),
              total: z.number().int().nullable(),
              moneda: z.enum(["PEN", "USD"]).nullable(),
              paginas: z.number().int().nullable(),
              /** Las tres partes que hacen completo a un expediente. */
              tieneComprobante: z.boolean(),
              tieneFicha: z.boolean(),
              vouchers: z.number().int(),
              completo: z.boolean(),
              /** Qué le falta, dicho para que se pueda pedir. */
              falta: z.string().nullable(),
            }),
          ),
        }),
        401: errorBody,
        404: errorBody,
      },
      summary: "Qué se subió en un lote y cuáles están completos",
    },

    confirmarCarga: {
      method: "POST",
      path: "/carga/:id/confirmar",
      pathParams: z.object({ id: z.string() }),
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          archivoId: z.string(),
        }),
        401: errorBody,
        422: errorBody,
      },
      summary: "Meterlo a la base y arrancar su lectura",
    },

    descartarCarga: {
      method: "POST",
      path: "/carga/:id/descartar",
      pathParams: z.object({ id: z.string() }),
      body: z.object({ motivo: z.string().max(300).nullish() }),
      responses: {
        200: z.object({ ok: z.literal(true) }),
        401: errorBody,
      },
      summary: "Tirar un archivo subido sin meterlo a la base",
    },

    recruzar: {
      method: "POST",
      path: "/cruzar",
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          cruces: z.number().int(),
          incidencias: z.number().int(),
          dice: z.string(),
        }),
        401: errorBody,
      },
      summary: "Volver a cruzar el periodo con lo que se corrigió a mano",
    },

    /**
     * Quitar la conciliación de un abono, la ató quien la ató.
     *
     * `deshacer` solo borra lo que decidió una persona —14 de 204 cruces—, así
     * que el 93 % de lo conciliado no se podía deshacer desde ninguna parte de
     * la aplicación: la única salida era `recruzar`, que rehace el periodo
     * entero y vuelve a poner el mismo cruce.
     *
     * Esto quita el cruce **sea de quien sea** y deja escrito que lo quitó una
     * persona, para que la siguiente pasada del motor NO lo vuelva a atar.
     * Quitar algo y que reaparezca solo es peor que no poder quitarlo, porque
     * la primera vez no se nota.
     */
    /**
     * Las últimas conciliaciones, y las que se deshicieron.
     *
     * Lo pidió contabilidad dos veces con las mismas palabras. `/auditoria` es
     * el registro de TODO y no responde «¿qué se conció esta mañana?» sin
     * saber qué filtro poner.
     *
     * Las deshechas salen de la bitácora y no de `cruce`, porque el cruce ya
     * no existe: es la única acción del sistema que borra una fila.
     */
    conciliaciones: {
      method: "GET",
      path: "/conciliaciones",
      query: z.object({
        que: z.enum(["hechas", "deshechas"]).default("hechas"),
        usuario: z.string().optional(),
        desde: z.string().optional(),
        pagina: z.coerce.number().int().min(0).default(0),
        porPagina: z.coerce.number().int().min(1).max(200).default(50),
      }),
      responses: {
        200: z.object({
          total: z.number().int(),
          filas: z.array(
            z.object({
              movimientoId: z.string(),
              momento: z.string(),
              /** Quién lo hizo, o la regla del motor si fue automático. */
              quien: z.string(),
              /** `true` cuando lo ató el cruce y no una persona. */
              automatico: z.boolean(),
              boleta: z.string().nullable(),
              cliente: z.string().nullable(),
              importe: z.number().int(),
              moneda: z.enum(["PEN", "USD"]),
              fecha: z.string().nullable(),
              banco: z.string().nullable(),
              regla: z.string().nullable(),
              /** Solo en las deshechas: por qué se quitó, si se dijo. */
              nota: z.string().nullable(),
            }),
          ),
          /** Para llenar el filtro con quien de verdad hizo algo. */
          usuarios: z.array(z.string()),
        }),
        401: errorBody,
      },
      summary: "Las últimas conciliaciones y las deshechas",
    },

    desvincular: {
      method: "POST",
      path: "/desvincular",
      body: z.object({
        movimientoId: z.string(),
        /** Solo esta boleta. Sin esto se quitan todas las de ese abono. */
        comprobanteId: z.string().nullish(),
        motivo: z.string().max(300).nullish(),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          quitados: z.number().int(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Quitar la conciliación de un abono para poder rectificarla",
    },

    deshacer: {
      method: "POST",
      path: "/decision/deshacer",
      body: z.object({ movimientoId: z.string() }),
      responses: { 200: decisionHechaSchema, 401: errorBody, 422: errorBody },
      summary: "Deshacer la última decisión sobre un movimiento",
    },

    seguimiento: {
      method: "GET",
      path: "/seguimiento/:movimientoId",
      pathParams: z.object({ movimientoId: z.string() }),
      responses: { 200: seguimientoSchema, 401: errorBody },
      summary: "Qué ha pasado con este movimiento, y quién lo decidió",
    },

    /**
     * La vista previa del archivo de CONCAR.
     *
     * Es el equivalente al panel del lote en el ERP del área: qué va a salir,
     * si cuadra, qué está mal y qué números de comprobante se van a consumir.
     * NO reserva correlativos: mirar no puede gastar numeración.
     */
    asientos: {
      method: "GET",
      path: "/asientos",
      responses: { 200: asientosSchema, 401: errorBody },
      summary:
        "Qué líneas van a salir para CONCAR, y qué falta para poder entregarlas",
    },

    cierre: {
      method: "GET",
      path: "/cierre",
      query: z.object({ moneda: z.enum(["PEN", "USD"]).default("PEN") }),
      responses: { 200: cierreSchema, 401: errorBody },
      summary: "Qué hay, qué sale y qué falta para cerrar el periodo",
    },

    /**
     * Las imágenes de voucher pegadas en más de una boleta.
     *
     * Contabilidad lo pidió como «verificar que las fotos no se repitan», y
     * medirlo dio el matiz: de 294 imágenes de voucher, 277 cumplen el 1 a 1 y
     * 17 no — y esas 17 no son un error de carga, son un pago que cubre varias
     * boletas. La pantalla tiene que decir cuál es cuál.
     */
    compartidas: {
      method: "GET",
      path: "/compartidas",
      responses: {
        200: z.object({
          /** Imágenes clasificadas como voucher y pegadas en alguna boleta. */
          imagenes: z.number().int(),
          /** Cuántas de esas están en una sola boleta. */
          unaSola: z.number().int(),
          /** Cuántas están en más de una. */
          repetidas: z.number().int(),
          /**
           * Un caso es un grupo de boletas atadas entre sí por una foto
           * compartida, con TODOS sus vouchers. No una foto suelta: una boleta
           * se paga con varios papeles, y mirar uno solo no contesta nada.
           */
          casos: z.array(
            z.object({
              id: z.string(),
              clase: z.enum([
                "respaldo_repetido",
                "falta_respaldo",
                "sobra_respaldo",
                "sin_totales",
                "cuadra",
              ]),
              dice: z.string(),
              moneda: z.enum(["PEN", "USD"]).nullable(),
              /** El cliente, cuando todas las boletas son del mismo. */
              cliente: z.string().nullable(),
              /** Lo que reclaman las boletas, en céntimos. */
              deben: z.number().int(),
              /** Lo que respaldan los papeles, cada uno contado una vez. */
              respaldan: z.number().int(),
              diferencia: z.number().int(),
              boletas: z.array(
                z.object({
                  comprobanteId: z.string(),
                  serieNumero: z.string().nullable(),
                  cliente: z.string().nullable(),
                  total: z.number().int().nullable(),
                  /** Si ya está cruzada con un abono. */
                  cruzada: z.boolean(),
                }),
              ),
              vouchers: z.array(
                z.object({
                  recorte: z.string(),
                  importe: z.number().int().nullable(),
                  /** En cuántas boletas del grupo está pegada esta foto. */
                  enBoletas: z.number().int(),
                  /** Su importe no puede ser cierto: hay que releerlo. */
                  sospechoso: z.boolean(),
                }),
              ),
            }),
          ),
        }),
        401: errorBody,
      },
      summary:
        "Los grupos de boletas atados por una foto de voucher compartida",
    },

    /**
     * Mandar al modelo lo que el lector óptico no pudo.
     *
     * Va como una acción aparte y explícita, y no dentro del pipeline, porque
     * ESTO SE COBRA por llamada. Subir un archivo no puede gastar dinero sin
     * que nadie lo haya pedido: el pipeline pasa el óptico —local y gratis— y
     * lo que queda se decide aquí, con el número de imágenes y el coste a la
     * vista antes de pulsar.
     */
    leerConModelo: {
      method: "POST",
      path: "/leer-con-modelo",
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          leidas: z.number().int(),
          resueltas: z.number().int(),
          costo: z.number(),
          dice: z.string(),
        }),
        401: errorBody,
        409: errorBody,
      },
      summary: "Leer con el modelo lo que el óptico no resolvió",
    },

    documentos: {
      method: "GET",
      path: "/documentos",
      query: z.object({
        /**
         * Qué mitad del tablero se pide.
         *
         * Son dos trabajos distintos con dos ritmos distintos —5 extractos al
         * mes contra 150 expedientes— y compartían un tablero en el que los 5
         * se perdían entre los 150. El extracto que falta es lo que impide
         * cerrar el mes, y era lo que menos se veía.
         *
         * Sin `clase` se devuelve todo, que es lo que había antes.
         */
        clase: z.enum(["comprobante", "estado_cuenta"]).optional(),
      }),
      responses: { 200: documentosSchema, 401: errorBody },
      summary: "En qué punto de la lectura está cada archivo",
    },

    fichaArchivo: {
      method: "GET",
      path: "/archivo/:id",
      pathParams: z.object({ id: z.string() }),
      responses: { 200: fichaArchivoSchema, 401: errorBody, 404: errorBody },
      summary: "Qué se hizo con este archivo y qué salió de él",
    },

    reintentarArchivo: {
      method: "POST",
      path: "/archivo/:id/reintentar",
      pathParams: z.object({ id: z.string() }),
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        422: errorBody,
      },
      summary:
        "Volver a intentar la etapa que falló, sin rehacer las anteriores",
    },

    reintentarFallados: {
      method: "POST",
      path: "/archivos/reintentar-fallados",
      body: z.object({ confirmo: z.literal(true) }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          cuantos: z.number().int(),
          dice: z.string(),
        }),
        401: errorBody,
      },
      summary: "Reencolar todos los archivos atascados",
    },

    ficha: {
      method: "GET",
      path: "/movimiento/:id",
      pathParams: z.object({ id: z.string() }),
      responses: { 200: fichaSchema, 401: errorBody, 404: errorBody },
      summary: "Todo lo que hace falta para decidir sobre un movimiento",
    },

    mesa: {
      method: "GET",
      path: "/mesa",
      query: z.object({
        // Las cinco bandejas que pinta la pantalla. Faltaban `todos` y
        // `prestamos`, que el router lleva implementados desde el principio y
        // el web pide desde el principio: la validación las rechazaba con un
        // 422 y la pestaña salía vacía, con los contadores en cero. O sea, un
        // mes entero con el mismo aspecto que un mes sin datos, y sin un solo
        // error a la vista.
        estado: z
          .enum([
            "todos",
            "por_decidir",
            "conciliados",
            "prestamos",
            "sin_respaldo",
          ])
          .default("por_decidir"),
        buscar: z.string().optional(),
        pagina: z.coerce.number().int().min(0).default(0),
        porPagina: z.coerce.number().int().min(1).max(200).default(20),
        // Junta las filas por banco y moneda, con su subtotal.
        agrupar: z.coerce.boolean().optional(),

        // LOS FILTROS QUE FALTABAN.
        //
        // La mesa solo sabía filtrar por bandeja y por texto: no había forma
        // de decir «solo el BCP en dólares de la primera quincena», que es
        // como se concilia de verdad — una cuenta entera de una vez.
        //
        // Son las mismas columnas que la tabla ya pinta. Un filtro por algo
        // que no se ve en pantalla no se le ocurre a nadie.
        banco: z.string().optional(),
        cuentaId: z.string().optional(),
        empresaId: z.string().optional(),
        moneda: z.enum(["PEN", "USD"]).optional(),
        /** Fechas del movimiento, inclusive, como 2026-01-31. */
        desde: z.string().optional(),
        hasta: z.string().optional(),
        /** En céntimos, como todo el dinero de este sistema. */
        importeMin: z.coerce.number().int().optional(),
        importeMax: z.coerce.number().int().optional(),
      }),
      responses: { 200: mesaSchema, 401: errorBody },
      summary: "Los movimientos que necesitan una decisión",
    },

    /**
     * Corregir movimientos de un extracto YA CARGADO.
     *
     * Existe porque contabilidad lo pidió —«poder editarlo después, si
     * quiero»— y hasta ahora solo se podía antes de confirmar. Lo que lo hacía
     * peligroso sigue siendo verdad, así que la regla es estrecha:
     *
     * UN MOVIMIENTO CONCILIADO NO SE TOCA. Está cruzado contra una boleta con
     * su importe aplicado y su firma; cambiarle la cifra por detrás dejaría ese
     * cruce apuntando a algo que ya no existe, y el descuadre aparecería
     * semanas después sin que nada dijera de dónde salió. Se rechaza diciendo
     * cuál es y con qué cruce, para poder deshacerlo primero.
     *
     * Se guarda en lote, como en la carga: editar tres celdas son tres cambios
     * de un mismo acto, y así el recálculo y el apunte en la bitácora se hacen
     * una vez sobre el conjunto.
     */
    expedientes: {
      method: "GET",
      path: "/expedientes",
      query: z.object({
        periodo: z.string().optional(),
        limite: z.coerce.number().int().min(1).max(500).optional(),
        offset: z.coerce.number().int().min(0).optional(),
        /** Busca en cliente, serie-número y nombre del archivo. */
        buscar: z.string().optional(),
        filtro: z
          .enum([
            "todos",
            "incompletos",
            "sinVoucher",
            "sinFicha",
            "fallados",
            // Los que no cuadran consigo mismos: el fichero dice una persona y
            // el papel otra, o dentro hay más de un comprobante.
            "discrepan",
          ])
          .optional(),
      }),
      responses: { 200: expedientesSchema, 401: errorBody },
      summary: "Los expedientes del mes y qué le falta a cada uno",
    },

    /**
     * Eliminar un estado de cuenta ya cargado, con todo lo que trajo.
     *
     * Existe para el caso que se da todos los meses: el banco entrega un PDF
     * mal —cortado, de la cuenta equivocada, o incompleto— y después manda el
     * bueno. Sin esto, el malo se queda dentro para siempre y sus movimientos
     * cuentan en el cierre.
     *
     * REQUIERE ROL DE JEFE. Borrar un extracto se lleva por delante sus
     * movimientos, y eso no es una corrección: es deshacer una carga. Va al
     * mismo nivel que desvincular un cruce o cerrar el periodo.
     *
     * NO SE BORRA SI ALGO ESTÁ CONCILIADO. Un movimiento cruzado tiene una
     * boleta aplicada contra él; borrarlo dejaría esa boleta cobrada contra
     * nada. Se dice cuántos son y hay que deshacer esos cruces primero — el
     * mismo criterio que para editarlos.
     */
    /**
     * Dejar movimientos fuera de la conciliación, o volver a meterlos.
     *
     * Un extracto trae líneas que NUNCA van a cruzar contra una boleta y aun
     * así hay que guardarlas: el ITF de dos soles, la comisión de
     * mantenimiento, el abono de céntimos de una pasarela. Mientras cuenten
     * como pendientes, el mes no se puede cerrar y la cola de revisión se llena
     * de casos que nadie va a resolver porque no hay nada que resolver.
     *
     * NO SE BORRAN, SE MARCAN. La línea sigue en la tabla, sigue sumando en el
     * cuadre y sigue en la cadena de saldos: lo único que cambia es que deja de
     * buscarse pareja. Borrarla descuadraría el extracto contra el PDF del
     * banco, que es la comprobación que sostiene todo lo demás.
     *
     * Se manda una lista porque así se usa: se filtran las de menos de veinte
     * soles y se marcan las cuarenta de una vez.
     */
    marcarConciliables: {
      method: "POST",
      path: "/extracto/:archivoId/conciliables",
      pathParams: z.object({ archivoId: z.string() }),
      body: z.object({
        /**
         * El tope da para un extracto entero A PROPÓSITO.
         *
         * Con 2.000 la mutación se quedaba corta justo cuando más falta hace:
         * marcar las 3.665 líneas de un extracto grande de una vez devolvía un
         * 422 de zod, y el gesto que la pantalla ofrece —«marcá todo y sacalo»—
         * no se podía cumplir. Son identificadores, no filas: 20.000 caben en
         * medio mega de cuerpo, y el handler ya lee el extracto entero de todas
         * formas para poder nombrar las líneas por su número.
         */
        ids: z.array(z.string()).min(1).max(20_000),
        /** `false` las saca de la conciliación; `true` las devuelve. */
        conciliable: z.boolean(),
        /** Por qué. Queda en la bitácora: es una decisión, no un dato leído. */
        motivo: z.string().max(300).optional(),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          cambiados: z.number().int(),
          /** Las que no se tocaron por estar ya cruzadas contra una boleta. */
          bloqueados: z.array(
            z.object({ id: z.string(), orden: z.number().int() }),
          ),
        }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        422: errorBody,
      },
      summary: "Dejar movimientos fuera de la conciliación, o devolverlos",
    },

    eliminarArchivo: {
      method: "POST",
      path: "/archivo/:id/eliminar",
      pathParams: z.object({ id: z.string() }),
      body: z.object({
        confirmo: z.literal(true),
        /** Por qué se borra. Queda en la bitácora: es una carga deshecha. */
        motivo: z.string().min(3).max(300),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          /** Cuántos movimientos se fueron con él. */
          movimientos: z.number().int(),
        }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Borrar un estado de cuenta cargado y todo lo que trajo",
    },

    editarMovimientos: {
      method: "POST",
      path: "/extracto/:archivoId/movimientos",
      pathParams: z.object({ archivoId: z.string() }),
      body: z.object({
        cambios: z
          .array(
            z.object({
              id: z.string(),
              fechaOper: z
                .string()
                .regex(/^\d{4}-\d{2}-\d{2}$/)
                .optional(),
              descripcion: z.string().max(500).nullish(),
              referencia: z.string().max(120).nullish(),
              nroOperacion: z.string().max(60).nullish(),
              abono: z.number().int().nonnegative().nullish(),
              cargo: z.number().int().nonnegative().nullish(),
              itf: z.number().int().nonnegative().nullish(),
              saldo: z.number().int().nullish(),
            }),
          )
          .min(1)
          .max(2000),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          cambiados: z.number().int(),
          /** Los que no se tocaron por estar conciliados, con su cruce. */
          bloqueados: z.array(
            z.object({
              id: z.string(),
              orden: z.number().int(),
              cruceId: z.string(),
            }),
          ),
          /** Recalculado sobre lo que quedó guardado. */
          cuadre: cuadreSchema.nullable(),
          hallazgos: z.array(hallazgoSchema),
        }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        422: errorBody,
      },
      summary: "Corregir movimientos de un extracto ya cargado",
    },

    extracto: {
      method: "GET",
      path: "/extracto/:archivoId",
      pathParams: z.object({ archivoId: z.string() }),
      query: z.object({
        limite: z.coerce.number().int().min(1).max(500).optional(),
        offset: z.coerce.number().int().min(0).optional(),
        /** Busca en descripción, referencia y número de operación. */
        buscar: z.string().optional(),
        /** `si` deja solo los conciliados, `no` solo los que no lo están. */
        conciliado: z.enum(["si", "no"]).optional(),
        /** `no` deja solo los que están fuera de la conciliación. */
        conciliable: z.enum(["si", "no"]).optional(),
        ...porImporte,
      }),
      responses: { 200: extractoSchema, 401: errorBody, 404: errorBody },
      summary: "Un estado de cuenta cargado, con sus movimientos",
    },

    /**
     * Qué líneas cumplen un criterio EN TODO EL EXTRACTO, no en una página.
     *
     * La tabla de la ficha viene paginada de 200 en 200, y los gestos de marcar
     * —«las menores a 10», «las salidas de 0 para arriba», el tilde de la
     * cabecera— se resolvían sobre las filas descargadas. En un extracto de
     * 3.665 líneas eso marcaba 200 y dejaba 3.465 dentro sin decirlo: había que
     * pasar página, volver a pulsar, dieciocho veces, y confiar en no saltarse
     * ninguna.
     *
     * Devuelve solo identificadores porque es lo único que hace falta: quien
     * llama ya tiene la página pintada y solo quiere saber a qué más alcanza el
     * criterio. Las que ya están cruzadas contra una boleta no salen nunca —esas
     * no se marcan en ningún sentido, y la casilla de su fila va deshabilitada.
     */
    idsDelExtracto: {
      method: "GET",
      path: "/extracto/:archivoId/ids",
      pathParams: z.object({ archivoId: z.string() }),
      query: z.object({
        /** Los mismos tres filtros de la tabla, para no marcar lo que no se ve. */
        buscar: z.string().optional(),
        conciliado: z.enum(["si", "no"]).optional(),
        conciliable: z.enum(["si", "no"]).optional(),
        /**
         * Céntimos. Deja solo las de importe menor —mira el abono Y el cargo,
         * que lo que no vale la pena conciliar es el movimiento pequeño.
         */
        menoresA: z.coerce.number().int().min(1).optional(),
        /**
         * Céntimos. Deja solo las SALIDAS de ese importe para arriba. Mira solo
         * el cargo, y con `0` son todas las salidas.
         */
        salidasDesde: z.coerce.number().int().min(0).optional(),
        // El mismo filtro por importe que la ficha: marcar tiene que alcanzar
        // exactamente lo que el filtro deja ver, ni una línea más.
        ...porImporte,
      }),
      responses: {
        200: z.object({
          ids: z.array(z.string()),
          /**
           * Cuántas cumplen de verdad. Es `ids.length` salvo que se pase del
           * tope de una tanda, y entonces la pantalla lo puede decir en vez de
           * mentir con un número corto.
           */
          total: z.number().int(),
        }),
        401: errorBody,
        404: errorBody,
      },
      summary: "Los identificadores de las líneas que cumplen un criterio",
    },

    cobertura: {
      method: "GET",
      path: "/cobertura",
      query: z.object({
        /** El mes, `AAAA-MM`. Sin él, el más reciente. */
        periodo: z.string().optional(),
      }),
      responses: { 200: coberturaSchema, 401: errorBody },
      summary:
        "Qué cuentas entregaron su estado de cuenta este mes y cuáles no",
    },

    /**
     * DAR DE ALTA, CORREGIR Y RETIRAR DEL MAESTRO (2026-09-09).
     *
     * El maestro llegaba solo del Excel del ERP y desde la web era de lectura.
     * Eso deja sin salida lo cotidiano: una razón social nueva, un número mal
     * tecleado, una cuenta que se cerró.
     *
     * ⚠️ REIMPORTAR EL EXCEL PISA PARTE DE ESTO. `cargar-maestros` sobrescribe
     * banco, moneda, CCI y empresa de cada cuenta que ya existe, y vuelve a
     * insertar las que se hayan borrado. Lo editado aquí vale hasta la
     * siguiente importación; lo dado de baja también reaparece.
     *
     * BORRAR ES LO ÚLTIMO, Y SOLO SI NO HAY NADA ENCIMA. Con movimientos,
     * extractos o cruces detrás, se DA DE BAJA: sale de las listas y el
     * historial se queda donde está. Un error recién cometido se borra; uno con
     * trabajo encima, no — borrarlo dejaría movimientos colgando de una cuenta
     * que ya no existe.
     */
    crearEmpresa: {
      method: "POST",
      path: "/maestros/empresa",
      body: z.object({
        ruc: z.string().regex(/^\d{11}$/, "El RUC son 11 dígitos"),
        razonSocial: z.string().min(2).max(200),
        nombreCorto: z.string().max(80).nullish(),
      }),
      responses: {
        200: z.object({ id: z.string(), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Dar de alta una razón social",
    },

    editarEmpresa: {
      method: "PATCH",
      path: "/maestros/empresa/:id",
      pathParams: z.object({ id: z.string() }),
      body: z.object({
        ruc: z
          .string()
          .regex(/^\d{11}$/, "El RUC son 11 dígitos")
          .optional(),
        razonSocial: z.string().min(2).max(200).optional(),
        nombreCorto: z.string().max(80).nullish(),
        /** Volver a activarla, si estaba dada de baja. */
        activa: z.boolean().optional(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Corregir una razón social",
    },

    retirarEmpresa: {
      method: "DELETE",
      path: "/maestros/empresa/:id",
      pathParams: z.object({ id: z.string() }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          /** `true` si se borró de verdad; `false` si solo se dio de baja. */
          borrada: z.boolean(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
      },
      summary: "Borrar una razón social, o darla de baja si tiene historial",
    },

    crearCuenta: {
      method: "POST",
      path: "/maestros/cuenta",
      body: z.object({
        empresaId: z.string().min(1),
        banco: z.string().min(2).max(40),
        numero: z.string().min(4).max(40),
        moneda: z.enum(["PEN", "USD"]),
        cci: z.string().max(40).nullish(),
        alias: z.string().max(80).nullish(),
      }),
      responses: {
        200: z.object({ id: z.string(), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Dar de alta una cuenta bancaria",
    },

    editarCuenta: {
      method: "PATCH",
      path: "/maestros/cuenta/:id",
      pathParams: z.object({ id: z.string() }),
      body: z.object({
        empresaId: z.string().min(1).optional(),
        banco: z.string().min(2).max(40).optional(),
        numero: z.string().min(4).max(40).optional(),
        moneda: z.enum(["PEN", "USD"]).optional(),
        cci: z.string().max(40).nullish(),
        alias: z.string().max(80).nullish(),
        activa: z.boolean().optional(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Corregir una cuenta bancaria",
    },

    retirarCuenta: {
      method: "DELETE",
      path: "/maestros/cuenta/:id",
      pathParams: z.object({ id: z.string() }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          borrada: z.boolean(),
          dice: z.string(),
        }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
      },
      summary: "Borrar una cuenta, o darla de baja si tiene historial",
    },

    /**
     * Corregir desde y hasta qué mes se le pide extracto a una cuenta.
     *
     * Es lo único editable del maestro desde la web, y a propósito: el resto
     * —banco, número, empresa— llega del ERP y cambiarlo aquí lo desincronizaría.
     * Esto no viene de ningún lado: es una decisión del área sobre qué esperar.
     */
    fijarEntrega: {
      method: "POST",
      path: "/maestros/cuenta/:cuentaId/entrega",
      pathParams: z.object({ cuentaId: z.string() }),
      body: z.object({
        desde: z
          .string()
          .regex(/^\d{4}-\d{2}$/, "el mes va como AAAA-MM")
          .nullable(),
        hasta: z
          .string()
          .regex(/^\d{4}-\d{2}$/, "el mes va como AAAA-MM")
          .nullable(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        404: errorBody,
        422: errorBody,
      },
      summary: "Desde qué mes esta cuenta entrega estado de cuenta",
    },

    maestros: {
      method: "GET",
      path: "/maestros",
      query: z.object({ buscar: z.string().optional() }),
      responses: { 200: maestrosSchema, 401: errorBody },
      summary: "De quién es cada cuenta, y de cuáles hay extracto",
    },

    /**
     * El expediente que toca conciliar, con sus boletas y sus vouchers.
     *
     * Sin `archivoId` devuelve el primero de la cola, que es como se entra
     * cuando alguien se sienta a trabajar y no viene de ningún sitio.
     */
    conciliarExpediente: {
      method: "GET",
      path: "/conciliar/expediente",
      query: z.object({
        archivoId: z.string().optional(),
        periodo: z.string().optional(),
        /**
         * Qué expedientes entran en la cola.
         *
         * `pendientes` —los que tienen algún voucher sin atar— es lo que se
         * quiere el 99 % de las veces. `todos` hace falta para volver sobre uno
         * ya terminado sin tener que buscarlo por otro camino.
         */
        cola: z.enum(["pendientes", "todos"]).default("pendientes"),
      }),
      responses: {
        200: z.object({
          /** `null` cuando no hay ninguno que conciliar. */
          expediente: expedienteAConciliarSchema.nullable(),
          /**
           * POR QUÉ no hay ninguno. Son tres situaciones distintas y la
           * pantalla tiene que decir cuál: «todavía no cargaste nada» y «ya
           * terminaste» se ven igual desde una lista vacía, y confundirlas
           * manda a buscar un fallo donde solo hay trabajo hecho.
           *
           * Y va como 200 con cuerpo, no como 204: un «sin contenido» con
           * cuerpo es una contradicción que el proxy de desarrollo convierte
           * en 502, y la pantalla se queda cargando para siempre.
           */
          vacio: z
            .enum(["sin_periodo", "sin_expedientes", "todo_atado"])
            .nullable(),
        }),
        401: errorBody,
        404: errorBody,
      },
      summary: "El expediente a conciliar, con sus vouchers y su cola",
    },

    /**
     * Los movimientos que podrían ser el pago de este voucher.
     *
     * NO decide: enseña candidatos y quien mira firma. La compuerta es la
     * misma que en la revisión guiada, y por el mismo motivo — si se pudieran
     * aplicar de un clic, leer se convertiría en aceptar lo que propone la
     * máquina.
     */
    candidatosDelVoucher: {
      method: "GET",
      path: "/conciliar/candidatos",
      query: z.object({
        recorte: z.string(),
        /**
         * La ventana, en días alrededor de la fecha del voucher.
         *
         * Los valores por defecto son los que el motor tiene medidos: el abono
         * aparece el mismo día del voucher o hasta siete después, NUNCA antes.
         * Sobre 603 candidatos con las dos fechas, una ventana simétrica de
         * quince días dejaba pasar 411 imposibles. Se puede ampliar hacia atrás
         * a mano —una fecha mal leída lo justifica—, y entonces esas filas
         * salen marcadas como anteriores al pago.
         */
        diasAntes: z.coerce.number().int().min(0).max(60).default(0),
        diasDespues: z.coerce.number().int().min(0).max(60).default(7),
        /** Tolerancia de importe en céntimos. 0 es coincidencia exacta. */
        tolerancia: z.coerce.number().int().min(0).max(1_000_000).default(500),
        /** Buscar en otra cuenta distinta de la que dice el voucher. */
        cuentaId: z.string().optional(),
        /** Ignorar la cuenta y buscar en las cuatro. Para Yape y Plin. */
        todasLasCuentas: siONo(false),
        ocultarConciliados: siONo(false),
        /** Texto libre sobre la glosa y el número de operación. */
        buscar: z.string().optional(),
        limite: z.coerce.number().int().min(1).max(100).default(25),
      }),
      responses: { 200: candidatosSchema, 401: errorBody, 404: errorBody },
      summary: "Los abonos que podrían ser este pago",
    },

    /**
     * Atar un voucher con el movimiento que lo pagó.
     *
     * Escribe DOS cosas, y las dos hacen falta:
     *
     *   · `enlace_pago`, que va por imagen y sobrevive al reproceso del cruce
     *     —el motor arranca borrando los cruces automáticos, y sin esto el
     *     trabajo manual se perdería en la siguiente pasada—.
     *   · el `cruce` movimiento↔boleta con origen `usuario`, que es lo que ve
     *     el resto del sistema: el estado del abono, el cierre y el asiento.
     */
    atarVoucher: {
      method: "POST",
      path: "/conciliar/atar",
      body: z.object({
        recorte: z.string().min(1),
        /**
         * Qué pago de la imagen. Una foto puede traer dos vouchers y cada uno
         * se ata por su cuenta; por defecto el primero, que es el caso normal
         * y lo que había antes de que existiera este campo.
         */
        pagoN: z.number().int().min(1).default(1),
        movimientoId: z.string().min(1),
        /**
         * Contra qué boleta se aplica. Obligatorio cuando el expediente trae
         * más de una: adivinarlo cerraría la boleta equivocada, que es el
         * error más caro de esta pantalla y el más difícil de ver después.
         */
        comprobanteId: z.string().min(1),
        nota: z.string().max(500).nullish(),
      }),
      responses: {
        200: z.object({
          ok: z.literal(true),
          dice: z.string(),
          /** Cuántos vouchers del expediente siguen sin atar. */
          quedan: z.number().int(),
        }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Atar un voucher con su movimiento bancario",
    },

    /** Deshacer lo anterior: suelta el voucher y quita el cruce que escribió. */
    desatarVoucher: {
      method: "POST",
      path: "/conciliar/desatar",
      body: z.object({
        recorte: z.string().min(1),
        /**
         * Qué pago de la imagen. Una foto puede traer dos vouchers y cada uno
         * se ata por su cuenta; por defecto el primero, que es el caso normal
         * y lo que había antes de que existiera este campo.
         */
        pagoN: z.number().int().min(1).default(1),
        nota: z.string().max(500).nullish(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Soltar un voucher de su movimiento",
    },

    /** «Este pago no está en ningún extracto cargado», que es una decisión. */
    marcarSinAbono: {
      method: "POST",
      path: "/conciliar/sin-abono",
      body: z.object({
        recorte: z.string().min(1),
        /**
         * Qué pago de la imagen. Una foto puede traer dos vouchers y cada uno
         * se ata por su cuenta; por defecto el primero, que es el caso normal
         * y lo que había antes de que existiera este campo.
         */
        pagoN: z.number().int().min(1).default(1),
        motivo: z.string().max(500).nullish(),
      }),
      responses: {
        200: z.object({ ok: z.literal(true), dice: z.string() }),
        401: errorBody,
        403: errorBody,
        409: errorBody,
        422: errorBody,
      },
      summary: "Dejar dicho que este pago no está en ningún extracto",
    },

    resumen: {
      method: "GET",
      path: "/resumen",
      responses: {
        200: resumenSchema,
        401: errorBody,
      },
      summary: "Conteos para la pantalla de inicio",
    },
  },
  {
    pathPrefix: "",
    commonResponses: {
      500: errorBody,
    },
  },
);

export type Contract = typeof contract;
